import {
  BookOpen, ScrollText, Cpu, Wrench, Pickaxe, Mountain, Microscope,
  ShieldCheck, Heart, Lightbulb, Wallet, Briefcase, Trophy, BarChart3,
  Leaf, Bot, Dna, Landmark, Brain, Sparkles, Network, ShieldAlert, Database
} from 'lucide-react';

export const LAB_CONTENT = {
  english: {
    title: 'English Language Lab',
    icon: BookOpen,
    color: '#e11d48',
    info: 'Master English grammar, vocabulary, and sentence structure through interactive exercises. Learn parts of speech, synonyms/antonyms, and sentence building.',
    missions: [
      { id: 1, name: 'Parts of Speech', description: 'Sort words into correct categories', type: 'categorize', stars: 1,
        instruction: 'Drag each word into the correct part of speech category:',
        categories: [
          { id: 'noun', label: 'Noun', color: '#3b82f6' },
          { id: 'verb', label: 'Verb', color: '#22c55e' },
          { id: 'adjective', label: 'Adjective', color: '#f59e0b' },
        ],
        items: [
          { id: 'e1', text: 'Mountain', category: 'noun' }, { id: 'e2', text: 'Beautiful', category: 'adjective' },
          { id: 'e3', text: 'Run', category: 'verb' }, { id: 'e4', text: 'Teacher', category: 'noun' },
          { id: 'e5', text: 'Bright', category: 'adjective' }, { id: 'e6', text: 'Sing', category: 'verb' },
          { id: 'e7', text: 'River', category: 'noun' }, { id: 'e8', text: 'Write', category: 'verb' },
          { id: 'e9', text: 'Tall', category: 'adjective' },
        ]
      },
      { id: 2, name: 'Synonyms Match', description: 'Match words with their synonyms', type: 'match', stars: 1,
        leftLabel: 'Word', rightLabel: 'Synonym',
        items: [
          { id: 's1', left: 'Happy', right: 'Joyful', match: 's1' },
          { id: 's2', left: 'Big', right: 'Large', match: 's2' },
          { id: 's3', left: 'Fast', right: 'Quick', match: 's3' },
          { id: 's4', left: 'Smart', right: 'Intelligent', match: 's4' },
          { id: 's5', left: 'Brave', right: 'Courageous', match: 's5' },
        ]
      },
      { id: 3, name: 'Antonyms Match', description: 'Match words with their opposites', type: 'match', stars: 1,
        leftLabel: 'Word', rightLabel: 'Antonym',
        items: [
          { id: 'a1', left: 'Hot', right: 'Cold', match: 'a1' },
          { id: 'a2', left: 'Day', right: 'Night', match: 'a2' },
          { id: 'a3', left: 'Happy', right: 'Sad', match: 'a3' },
          { id: 'a4', left: 'Strong', right: 'Weak', match: 'a4' },
          { id: 'a5', left: 'Light', right: 'Dark', match: 'a5' },
        ]
      },
      { id: 4, name: 'Sentence Order', description: 'Arrange words to form a sentence', type: 'order', stars: 2,
        instruction: 'Arrange these words to form a correct sentence:',
        items: [
          { id: 'w1', text: 'The', order: 1 }, { id: 'w2', text: 'quick', order: 2 },
          { id: 'w3', text: 'brown', order: 3 }, { id: 'w4', text: 'fox', order: 4 },
          { id: 'w5', text: 'jumps', order: 5 }, { id: 'w6', text: 'over', order: 6 },
          { id: 'w7', text: 'the', order: 7 }, { id: 'w8', text: 'lazy', order: 8 },
          { id: 'w9', text: 'dog', order: 9 },
        ]
      },
      { id: 5, name: 'Grammar Quiz', description: 'Test your grammar knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'Which is a proper noun?', options: ['dog', 'India', 'running', 'beautiful'], answer: 'India' },
          { question: '"She sings well." What is "well"?', options: ['Noun', 'Verb', 'Adverb', 'Adjective'], answer: 'Adverb' },
          { question: 'Past tense of "go"?', options: ['goed', 'went', 'gone', 'going'], answer: 'went' },
          { question: 'Which sentence is correct?', options: ['He go school', 'He goes to school', 'He going school', 'He go to school'], answer: 'He goes to school' },
          { question: 'Plural of "child"?', options: ['childs', 'childrens', 'children', 'childes'], answer: 'children' },
        ]
      },
      { id: 6, name: 'Fill the Blanks', description: 'Complete sentences with the right word', type: 'fill-blank', stars: 2,
        instruction: 'Drag the correct word to complete each sentence:',
        items: [
          { id: 'efb1', sentence: 'She ___ to the market every morning.', answer: 'goes' },
          { id: 'efb2', sentence: 'The plural of "mouse" is ___.', answer: 'mice' },
          { id: 'efb3', sentence: 'An ___ is a word that describes a noun.', answer: 'adjective' },
          { id: 'efb4', sentence: 'The past tense of "eat" is ___.', answer: 'ate' },
          { id: 'efb5', sentence: 'A ___ is a group of words that makes complete sense.', answer: 'sentence' },
        ],
        distractors: ['verb', 'mouses', 'ran']
      },
      { id: 7, name: 'Grammar True or False', description: 'Rapid fire grammar facts', type: 'true-false', stars: 3,
        statements: [
          { id: 'etf1', text: 'A noun is a naming word.', answer: true },
          { id: 'etf2', text: '"Quickly" is an adjective.', answer: false },
          { id: 'etf3', text: 'Every sentence must have a verb.', answer: true },
          { id: 'etf4', text: '"They" is a pronoun.', answer: true },
          { id: 'etf5', text: 'An adverb modifies a noun.', answer: false },
          { id: 'etf6', text: '"Ran" is past tense of "run".', answer: true },
          { id: 'etf7', text: 'A comma is used to end a sentence.', answer: false },
          { id: 'etf8', text: '"Feet" is the plural of "foot".', answer: true },
        ]
      },
      { id: 8, name: 'Verb Tenses', description: 'Categorize verbs by tense', type: 'categorize', stars: 2,
        categories: [
          { id: 'past', label: 'Past Tense', color: '#3b82f6' },
          { id: 'present', label: 'Present Tense', color: '#22c55e' },
          { id: 'future', label: 'Future Tense', color: '#f59e0b' },
        ],
        items: [
          { id: 'evt1', text: 'Walked', category: 'past' }, { id: 'evt2', text: 'Runs', category: 'present' },
          { id: 'evt3', text: 'Will eat', category: 'future' }, { id: 'evt4', text: 'Played', category: 'past' },
          { id: 'evt5', text: 'Reads', category: 'present' }, { id: 'evt6', text: 'Shall go', category: 'future' },
          { id: 'evt7', text: 'Wrote', category: 'past' }, { id: 'evt8', text: 'Sings', category: 'present' },
          { id: 'evt9', text: 'Will learn', category: 'future' },
        ]
      },
      { id: 9, name: 'Punctuation Match', description: 'Match punctuation marks to their uses', type: 'match', stars: 2,
        leftLabel: 'Punctuation', rightLabel: 'Use',
        items: [
          { id: 'epm1', left: 'Full Stop (.)', right: 'Ends a statement', match: 'epm1' },
          { id: 'epm2', left: 'Question Mark (?)', right: 'Ends a question', match: 'epm2' },
          { id: 'epm3', left: 'Exclamation (!)', right: 'Shows strong emotion', match: 'epm3' },
          { id: 'epm4', left: 'Comma (,)', right: 'Separates items in a list', match: 'epm4' },
          { id: 'epm5', left: 'Apostrophe (\')', right: 'Shows possession or contraction', match: 'epm5' },
        ]
      },
      { id: 10, name: 'Vocabulary Quiz', description: 'Advanced vocabulary challenge', type: 'quiz', stars: 3,
        questions: [
          { question: 'What does "benevolent" mean?', options: ['Cruel', 'Kind', 'Lazy', 'Angry'], answer: 'Kind' },
          { question: '"Nocturnal" relates to?', options: ['Day', 'Night', 'Water', 'Fire'], answer: 'Night' },
          { question: 'An "autobiography" is written by?', options: ['A friend', 'The person themselves', 'A teacher', 'A publisher'], answer: 'The person themselves' },
          { question: 'What is a "metaphor"?', options: ['A type of poem', 'A comparison without like/as', 'A question mark', 'A verb form'], answer: 'A comparison without like/as' },
          { question: '"Ambiguous" means?', options: ['Clear', 'Uncertain', 'Bright', 'Heavy'], answer: 'Uncertain' },
        ]
      },
    ]
  },

  sanskrit: {
    title: 'Sanskrit Language Lab',
    icon: ScrollText,
    color: '#d97706',
    info: 'Learn Sanskrit through interactive exercises — match words to meanings, explore vibhakti (cases), and arrange shloka words in correct order.',
    missions: [
      { id: 1, name: 'Basic Vocabulary', description: 'Match Sanskrit words to English', type: 'match', stars: 1,
        leftLabel: 'Sanskrit', rightLabel: 'English',
        items: [
          { id: 'sk1', left: 'Jalam (जलम्)', right: 'Water', match: 'sk1' },
          { id: 'sk2', left: 'Agni (अग्नि)', right: 'Fire', match: 'sk2' },
          { id: 'sk3', left: 'Vayu (वायु)', right: 'Wind', match: 'sk3' },
          { id: 'sk4', left: 'Prithvi (पृथ्वी)', right: 'Earth', match: 'sk4' },
          { id: 'sk5', left: 'Surya (सूर्य)', right: 'Sun', match: 'sk5' },
          { id: 'sk6', left: 'Chandra (चन्द्र)', right: 'Moon', match: 'sk6' },
        ]
      },
      { id: 2, name: 'Vibhakti (Cases)', description: 'Match vibhakti to their usage', type: 'match', stars: 2,
        leftLabel: 'Vibhakti', rightLabel: 'Usage',
        items: [
          { id: 'v1', left: 'Prathama (प्रथमा)', right: 'Subject (Doer)', match: 'v1' },
          { id: 'v2', left: 'Dvitiya (द्वितीया)', right: 'Object (Karma)', match: 'v2' },
          { id: 'v3', left: 'Tritiya (तृतीया)', right: 'Instrument (By/With)', match: 'v3' },
          { id: 'v4', left: 'Chaturthi (चतुर्थी)', right: 'Purpose (For/To)', match: 'v4' },
          { id: 'v5', left: 'Panchami (पञ्चमी)', right: 'Source (From)', match: 'v5' },
        ]
      },
      { id: 3, name: 'Number Forms', description: 'Categorize words by number', type: 'categorize', stars: 1,
        categories: [
          { id: 'eka', label: 'Ekavachanam (Singular)', color: '#3b82f6' },
          { id: 'dvi', label: 'Dvivachanam (Dual)', color: '#22c55e' },
          { id: 'bahu', label: 'Bahuvachanam (Plural)', color: '#f59e0b' },
        ],
        items: [
          { id: 'n1', text: 'Ramah (रामः)', category: 'eka' }, { id: 'n2', text: 'Ramau (रामौ)', category: 'dvi' },
          { id: 'n3', text: 'Ramah (रामाः)', category: 'bahu' }, { id: 'n4', text: 'Balakah (बालकः)', category: 'eka' },
          { id: 'n5', text: 'Balakau (बालकौ)', category: 'dvi' }, { id: 'n6', text: 'Balakah (बालकाः)', category: 'bahu' },
        ]
      },
      { id: 4, name: 'Shloka Order', description: 'Arrange the Gayatri Mantra words', type: 'order', stars: 2,
        instruction: 'Arrange the words of this famous shloka in correct order:',
        items: [
          { id: 'sh1', text: 'Om', order: 1 }, { id: 'sh2', text: 'Bhur', order: 2 },
          { id: 'sh3', text: 'Bhuvah', order: 3 }, { id: 'sh4', text: 'Svah', order: 4 },
          { id: 'sh5', text: 'Tat', order: 5 }, { id: 'sh6', text: 'Savitur', order: 6 },
          { id: 'sh7', text: 'Varenyam', order: 7 },
        ]
      },
      { id: 5, name: 'Sanskrit Quiz', description: 'Test your Sanskrit knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'What does "Vidya" (विद्या) mean?', options: ['Wealth', 'Knowledge', 'Power', 'Beauty'], answer: 'Knowledge' },
          { question: '"Aham" (अहम्) means?', options: ['You', 'He', 'I', 'We'], answer: 'I' },
          { question: 'Sanskrit is also known as?', options: ['Dev Bhasha', 'Jan Bhasha', 'Lok Bhasha', 'Raj Bhasha'], answer: 'Dev Bhasha' },
          { question: 'How many vibhaktis in Sanskrit?', options: ['5', '6', '7', '8'], answer: '7' },
          { question: '"Pushpam" (पुष्पम्) means?', options: ['Fruit', 'Flower', 'Leaf', 'Tree'], answer: 'Flower' },
        ]
      },
      { id: 6, name: 'Sentence Completion', description: 'Fill blanks with correct Sanskrit terms', type: 'fill-blank', stars: 2,
        instruction: 'Complete each sentence with the correct Sanskrit term:',
        items: [
          { id: 'sfb1', sentence: 'The Sanskrit word for "king" is ___.', answer: 'Raja' },
          { id: 'sfb2', sentence: '"Namah" in "Om Namah Shivaya" means ___.', answer: 'salutation' },
          { id: 'sfb3', sentence: 'Sanskrit has ___ vibhaktis (cases).', answer: 'seven' },
          { id: 'sfb4', sentence: 'The Devanagari script is used to write ___.', answer: 'Sanskrit' },
          { id: 'sfb5', sentence: '"Guru" literally means ___ of darkness.', answer: 'remover' },
        ],
        distractors: ['eight', 'Hindi', 'creator']
      },
      { id: 7, name: 'Sanskrit Fact Check', description: 'True or False about Sanskrit', type: 'true-false', stars: 3,
        statements: [
          { id: 'stf1', text: 'Sanskrit is one of the oldest languages in the world.', answer: true },
          { id: 'stf2', text: 'Sanskrit has only 3 vachanams (numbers).', answer: true },
          { id: 'stf3', text: '"Devi" means "God" in Sanskrit.', answer: false },
          { id: 'stf4', text: 'The Rigveda is written in Sanskrit.', answer: true },
          { id: 'stf5', text: 'Sanskrit uses the Roman alphabet.', answer: false },
          { id: 'stf6', text: '"Vidyalaya" means "school" or "place of learning".', answer: true },
          { id: 'stf7', text: 'There are 5 vibhaktis in Sanskrit.', answer: false },
          { id: 'stf8', text: '"Tvam" means "you" in Sanskrit.', answer: true },
        ]
      },
      { id: 8, name: 'Sandhi Rules', description: 'Match sandhi types to their rules', type: 'match', stars: 2,
        leftLabel: 'Sandhi Type', rightLabel: 'Rule',
        items: [
          { id: 'snd1', left: 'Svara Sandhi', right: 'Vowel combinations', match: 'snd1' },
          { id: 'snd2', left: 'Vyanjana Sandhi', right: 'Consonant combinations', match: 'snd2' },
          { id: 'snd3', left: 'Visarga Sandhi', right: 'Visarga (ः) changes', match: 'snd3' },
          { id: 'snd4', left: 'Guna Sandhi', right: 'a/aa + i/ii = e', match: 'snd4' },
          { id: 'snd5', left: 'Vriddhi Sandhi', right: 'a/aa + e/ai = ai', match: 'snd5' },
        ]
      },
      { id: 9, name: 'Sanskrit Verb Forms', description: 'Categorize verbs by tense (lakaar)', type: 'categorize', stars: 2,
        categories: [
          { id: 'lat', label: 'Lat Lakaar (Present)', color: '#3b82f6' },
          { id: 'lang', label: 'Lang Lakaar (Past)', color: '#22c55e' },
          { id: 'lrt', label: 'Lrt Lakaar (Future)', color: '#f59e0b' },
        ],
        items: [
          { id: 'sv1', text: 'Gacchati (goes)', category: 'lat' }, { id: 'sv2', text: 'Agacchat (went)', category: 'lang' },
          { id: 'sv3', text: 'Gamishyati (will go)', category: 'lrt' }, { id: 'sv4', text: 'Pathati (reads)', category: 'lat' },
          { id: 'sv5', text: 'Apathat (read)', category: 'lang' }, { id: 'sv6', text: 'Pathishyati (will read)', category: 'lrt' },
          { id: 'sv7', text: 'Likhati (writes)', category: 'lat' }, { id: 'sv8', text: 'Alikhat (wrote)', category: 'lang' },
        ]
      },
      { id: 10, name: 'Sanskrit Literature Quiz', description: 'Test knowledge of Sanskrit texts', type: 'quiz', stars: 3,
        questions: [
          { question: 'Who wrote Meghadootam?', options: ['Valmiki', 'Kalidasa', 'Vyasa', 'Panini'], answer: 'Kalidasa' },
          { question: 'The Ramayana was written by?', options: ['Kalidasa', 'Valmiki', 'Tulsidas', 'Vyasa'], answer: 'Valmiki' },
          { question: 'Ashtadhyayi is a treatise on?', options: ['Medicine', 'Grammar', 'Mathematics', 'Astronomy'], answer: 'Grammar' },
          { question: 'Panini is famous for?', options: ['Poetry', 'Grammar rules', 'Epic writing', 'Music'], answer: 'Grammar rules' },
          { question: 'The Mahabharata was composed by?', options: ['Valmiki', 'Kalidasa', 'Vyasa', 'Chanakya'], answer: 'Vyasa' },
        ]
      },
    ]
  },

  'computer-science': {
    title: 'Computer Science Lab',
    icon: Cpu,
    color: '#64748b',
    info: 'Learn computing fundamentals — understand hardware vs software, binary numbers, logic gates, and basic programming concepts.',
    missions: [
      { id: 1, name: 'Hardware vs Software', description: 'Categorize components correctly', type: 'categorize', stars: 1,
        categories: [
          { id: 'hardware', label: 'Hardware', color: '#3b82f6' },
          { id: 'software', label: 'Software', color: '#22c55e' },
        ],
        items: [
          { id: 'cs1', text: 'Keyboard', category: 'hardware' }, { id: 'cs2', text: 'Windows OS', category: 'software' },
          { id: 'cs3', text: 'RAM', category: 'hardware' }, { id: 'cs4', text: 'Google Chrome', category: 'software' },
          { id: 'cs5', text: 'Monitor', category: 'hardware' }, { id: 'cs6', text: 'MS Word', category: 'software' },
          { id: 'cs7', text: 'CPU', category: 'hardware' }, { id: 'cs8', text: 'Python', category: 'software' },
        ]
      },
      { id: 2, name: 'Computer Terms', description: 'Match terms to definitions', type: 'match', stars: 1,
        items: [
          { id: 'ct1', left: 'CPU', right: 'Brain of the computer', match: 'ct1' },
          { id: 'ct2', left: 'RAM', right: 'Temporary memory', match: 'ct2' },
          { id: 'ct3', left: 'Hard Drive', right: 'Permanent storage', match: 'ct3' },
          { id: 'ct4', left: 'GPU', right: 'Graphics processor', match: 'ct4' },
          { id: 'ct5', left: 'Router', right: 'Network device', match: 'ct5' },
        ]
      },
      { id: 3, name: 'Boot Process', description: 'Order the computer startup steps', type: 'order', stars: 2,
        items: [
          { id: 'bp1', text: 'Press power button', order: 1 }, { id: 'bp2', text: 'BIOS/POST check runs', order: 2 },
          { id: 'bp3', text: 'Boot loader loads', order: 3 }, { id: 'bp4', text: 'Operating System starts', order: 4 },
          { id: 'bp5', text: 'Desktop appears', order: 5 }, { id: 'bp6', text: 'User logs in', order: 6 },
        ]
      },
      { id: 4, name: 'CS Quiz', description: 'Test your computer science knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'What does CPU stand for?', options: ['Central Processing Unit', 'Central Power Unit', 'Computer Personal Unit', 'Core Processing Unit'], answer: 'Central Processing Unit' },
          { question: 'Binary code uses which digits?', options: ['0 and 1', '0 to 9', '1 to 10', 'A to Z'], answer: '0 and 1' },
          { question: '1 byte = how many bits?', options: ['4', '8', '16', '32'], answer: '8' },
          { question: 'Which is an input device?', options: ['Monitor', 'Printer', 'Keyboard', 'Speaker'], answer: 'Keyboard' },
          { question: 'What is an algorithm?', options: ['A computer part', 'Step-by-step instructions', 'A programming language', 'A type of virus'], answer: 'Step-by-step instructions' },
        ]
      },
      { id: 5, name: 'CS Fill the Blanks', description: 'Complete computing concepts', type: 'fill-blank', stars: 2,
        instruction: 'Drag the correct term to complete each statement:',
        items: [
          { id: 'csfb1', sentence: 'The ___ is the brain of the computer.', answer: 'CPU' },
          { id: 'csfb2', sentence: 'RAM stands for Random ___ Memory.', answer: 'Access' },
          { id: 'csfb3', sentence: 'A ___ converts high-level code to machine code.', answer: 'compiler' },
          { id: 'csfb4', sentence: 'The smallest unit of data in computing is a ___.', answer: 'bit' },
          { id: 'csfb5', sentence: 'An ___ system manages computer hardware and software.', answer: 'operating' },
        ],
        distractors: ['byte', 'GPU', 'keyboard']
      },
      { id: 6, name: 'CS True or False', description: 'Rapid fire computing facts', type: 'true-false', stars: 3,
        statements: [
          { id: 'cstf1', text: 'A mouse is an output device.', answer: false },
          { id: 'cstf2', text: 'ROM retains data even when power is off.', answer: true },
          { id: 'cstf3', text: '1 KB = 1024 bytes.', answer: true },
          { id: 'cstf4', text: 'Python is a hardware component.', answer: false },
          { id: 'cstf5', text: 'A firewall helps protect against cyber threats.', answer: true },
          { id: 'cstf6', text: 'HTML is a programming language.', answer: false },
          { id: 'cstf7', text: 'USB stands for Universal Serial Bus.', answer: true },
          { id: 'cstf8', text: 'An SSD is slower than a traditional hard drive.', answer: false },
        ]
      },
      { id: 7, name: 'Storage Units Order', description: 'Order data storage units by size', type: 'order', stars: 2,
        items: [
          { id: 'cso1', text: 'Bit', order: 1 }, { id: 'cso2', text: 'Byte', order: 2 },
          { id: 'cso3', text: 'Kilobyte (KB)', order: 3 }, { id: 'cso4', text: 'Megabyte (MB)', order: 4 },
          { id: 'cso5', text: 'Gigabyte (GB)', order: 5 }, { id: 'cso6', text: 'Terabyte (TB)', order: 6 },
          { id: 'cso7', text: 'Petabyte (PB)', order: 7 },
        ]
      },
      { id: 8, name: 'Programming Concepts', description: 'Match programming terms to definitions', type: 'match', stars: 2,
        items: [
          { id: 'cspc1', left: 'Variable', right: 'Named container for data', match: 'cspc1' },
          { id: 'cspc2', left: 'Loop', right: 'Repeats a block of code', match: 'cspc2' },
          { id: 'cspc3', left: 'Function', right: 'Reusable block of code', match: 'cspc3' },
          { id: 'cspc4', left: 'Array', right: 'Ordered collection of items', match: 'cspc4' },
          { id: 'cspc5', left: 'Conditional', right: 'If-else decision making', match: 'cspc5' },
        ]
      },
      { id: 9, name: 'Input vs Output', description: 'Categorize computer peripherals', type: 'categorize', stars: 1,
        categories: [
          { id: 'input', label: 'Input Devices', color: '#3b82f6' },
          { id: 'output', label: 'Output Devices', color: '#22c55e' },
          { id: 'storage', label: 'Storage Devices', color: '#f59e0b' },
        ],
        items: [
          { id: 'csio1', text: 'Keyboard', category: 'input' }, { id: 'csio2', text: 'Monitor', category: 'output' },
          { id: 'csio3', text: 'USB Drive', category: 'storage' }, { id: 'csio4', text: 'Scanner', category: 'input' },
          { id: 'csio5', text: 'Printer', category: 'output' }, { id: 'csio6', text: 'Hard Disk', category: 'storage' },
          { id: 'csio7', text: 'Microphone', category: 'input' }, { id: 'csio8', text: 'Speaker', category: 'output' },
          { id: 'csio9', text: 'SSD', category: 'storage' },
        ]
      },
      { id: 10, name: 'Internet & Networking Quiz', description: 'Test your networking knowledge', type: 'quiz', stars: 3,
        questions: [
          { question: 'What does URL stand for?', options: ['Universal Resource Locator', 'Uniform Resource Locator', 'United Resource Link', 'Universal Record Link'], answer: 'Uniform Resource Locator' },
          { question: 'Which protocol is used for secure websites?', options: ['HTTP', 'FTP', 'HTTPS', 'SMTP'], answer: 'HTTPS' },
          { question: 'What is an IP address?', options: ['Internet Password', 'Unique device address on network', 'A web browser', 'A programming language'], answer: 'Unique device address on network' },
          { question: 'Wi-Fi stands for?', options: ['Wireless Fidelity', 'Wide Frequency', 'Wired Finder', 'Web Interface'], answer: 'Wireless Fidelity' },
          { question: 'What does LAN stand for?', options: ['Large Area Network', 'Local Access Node', 'Local Area Network', 'Long Area Network'], answer: 'Local Area Network' },
        ]
      },
    ]
  },

  plumbing: {
    title: 'Plumbing Workshop',
    icon: Wrench,
    color: '#0ea5e9',
    info: 'Learn plumbing basics — identify tools, understand pipe systems, and learn how to diagnose and fix common plumbing problems safely.',
    missions: [
      { id: 1, name: 'Tool Identification', description: 'Match plumbing tools to their uses', type: 'match', stars: 1,
        items: [
          { id: 'pt1', left: 'Pipe Wrench', right: 'Grip and turn pipes', match: 'pt1' },
          { id: 'pt2', left: 'Plunger', right: 'Unclog drains', match: 'pt2' },
          { id: 'pt3', left: 'Teflon Tape', right: 'Seal threaded joints', match: 'pt3' },
          { id: 'pt4', left: 'Plumber Snake', right: 'Clear deep blockages', match: 'pt4' },
          { id: 'pt5', left: 'Pipe Cutter', right: 'Cut pipes to size', match: 'pt5' },
        ]
      },
      { id: 2, name: 'Fix a Leaky Faucet', description: 'Arrange repair steps in order', type: 'order', stars: 2,
        items: [
          { id: 'lf1', text: 'Turn off water supply', order: 1 }, { id: 'lf2', text: 'Remove faucet handle', order: 2 },
          { id: 'lf3', text: 'Replace washer/O-ring', order: 3 }, { id: 'lf4', text: 'Reassemble faucet', order: 4 },
          { id: 'lf5', text: 'Turn on water supply', order: 5 }, { id: 'lf6', text: 'Test for leaks', order: 6 },
        ]
      },
      { id: 3, name: 'Pipe Types', description: 'Categorize pipe materials', type: 'categorize', stars: 1,
        categories: [
          { id: 'metal', label: 'Metal Pipes', color: '#64748b' },
          { id: 'plastic', label: 'Plastic Pipes', color: '#22c55e' },
        ],
        items: [
          { id: 'pp1', text: 'Copper', category: 'metal' }, { id: 'pp2', text: 'PVC', category: 'plastic' },
          { id: 'pp3', text: 'Galvanized Steel', category: 'metal' }, { id: 'pp4', text: 'CPVC', category: 'plastic' },
          { id: 'pp5', text: 'Cast Iron', category: 'metal' }, { id: 'pp6', text: 'PEX', category: 'plastic' },
        ]
      },
      { id: 4, name: 'Plumbing Quiz', description: 'Test your plumbing knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'What is a P-trap used for?', options: ['Catching debris', 'Preventing sewer gas', 'Increasing pressure', 'Heating water'], answer: 'Preventing sewer gas' },
          { question: 'Hot water pipe is typically colored?', options: ['Blue', 'Red', 'Green', 'Yellow'], answer: 'Red' },
          { question: 'Main shutoff valve controls?', options: ['Only kitchen', 'Only bathroom', 'Entire house water', 'Only garden'], answer: 'Entire house water' },
          { question: 'What causes water hammer?', options: ['Low pressure', 'Sudden valve closure', 'Cold weather', 'Hard water'], answer: 'Sudden valve closure' },
        ]
      },
      { id: 5, name: 'Plumbing Concepts', description: 'Fill in the plumbing blanks', type: 'fill-blank', stars: 2,
        instruction: 'Complete each plumbing concept:',
        items: [
          { id: 'pfb1', sentence: 'A ___ trap prevents sewer gases from entering the home.', answer: 'P-trap' },
          { id: 'pfb2', sentence: 'Water pressure is measured in ___ (pounds per square inch).', answer: 'PSI' },
          { id: 'pfb3', sentence: '___ tape is used to seal pipe thread connections.', answer: 'Teflon' },
          { id: 'pfb4', sentence: 'A ___ valve controls the main water supply to a house.', answer: 'shutoff' },
        ],
        distractors: ['S-trap', 'rubber', 'GPM']
      },
      { id: 6, name: 'Plumbing Fact Check', description: 'True or False plumbing knowledge', type: 'true-false', stars: 3,
        statements: [
          { id: 'ptf1', text: 'PVC pipes can be used for hot water supply.', answer: false },
          { id: 'ptf2', text: 'A plunger uses air pressure to unclog drains.', answer: true },
          { id: 'ptf3', text: 'Copper pipes are resistant to corrosion.', answer: true },
          { id: 'ptf4', text: 'All pipes in a home carry drinking water.', answer: false },
          { id: 'ptf5', text: 'Water heaters should be drained annually for maintenance.', answer: true },
          { id: 'ptf6', text: 'PEX piping is flexible and can bend around corners.', answer: true },
        ]
      },
      { id: 7, name: 'Water System Layout', description: 'Order how water reaches your tap', type: 'order', stars: 2,
        items: [
          { id: 'pws1', text: 'Water collected at reservoir', order: 1 }, { id: 'pws2', text: 'Water treatment plant purifies', order: 2 },
          { id: 'pws3', text: 'Pumped through main pipelines', order: 3 }, { id: 'pws4', text: 'Reaches overhead tank', order: 4 },
          { id: 'pws5', text: 'Distributed through internal pipes', order: 5 }, { id: 'pws6', text: 'Water flows from tap', order: 6 },
        ]
      },
      { id: 8, name: 'Plumbing Safety', description: 'Match safety rules to their purpose', type: 'match', stars: 2,
        items: [
          { id: 'psa1', left: 'Wear gloves', right: 'Protect hands from chemicals', match: 'psa1' },
          { id: 'psa2', left: 'Turn off main valve', right: 'Prevent flooding during repairs', match: 'psa2' },
          { id: 'psa3', left: 'Use goggles', right: 'Protect eyes from debris', match: 'psa3' },
          { id: 'psa4', left: 'Ventilate area', right: 'Avoid inhaling sewer gas', match: 'psa4' },
          { id: 'psa5', left: 'Check for gas leaks', right: 'Prevent explosions near pipes', match: 'psa5' },
        ]
      },
      { id: 9, name: 'Common Problems', description: 'Categorize plumbing issues by severity', type: 'categorize', stars: 2,
        categories: [
          { id: 'minor', label: 'Minor (DIY Fix)', color: '#22c55e' },
          { id: 'major', label: 'Major (Call Plumber)', color: '#ef4444' },
        ],
        items: [
          { id: 'pcp1', text: 'Dripping faucet', category: 'minor' }, { id: 'pcp2', text: 'Burst pipe', category: 'major' },
          { id: 'pcp3', text: 'Slow drain', category: 'minor' }, { id: 'pcp4', text: 'Sewer line backup', category: 'major' },
          { id: 'pcp5', text: 'Running toilet', category: 'minor' }, { id: 'pcp6', text: 'No hot water', category: 'major' },
          { id: 'pcp7', text: 'Loose faucet handle', category: 'minor' }, { id: 'pcp8', text: 'Water heater leaking', category: 'major' },
        ]
      },
      { id: 10, name: 'Advanced Plumbing Quiz', description: 'Advanced plumbing knowledge test', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is the purpose of a vent pipe?', options: ['Drain water', 'Allow air flow in drain system', 'Heat water', 'Filter water'], answer: 'Allow air flow in drain system' },
          { question: 'CPVC is best for?', options: ['Cold water only', 'Hot and cold water', 'Gas lines', 'Drainage'], answer: 'Hot and cold water' },
          { question: 'What tool clears deep drain blockages?', options: ['Plunger', 'Wrench', 'Plumber snake', 'Hammer'], answer: 'Plumber snake' },
          { question: 'What does GPM stand for?', options: ['Gallons Per Minute', 'Gas Pipe Material', 'General Plumbing Method', 'Ground Pipe Measure'], answer: 'Gallons Per Minute' },
          { question: 'A backflow preventer protects against?', options: ['Leaks', 'Contaminated water reversal', 'Pipe bursts', 'Corrosion'], answer: 'Contaminated water reversal' },
        ]
      },
    ]
  },

  mining: {
    title: 'Mining & Resources Lab',
    icon: Pickaxe,
    color: '#b45309',
    info: 'Explore the world of mining — learn about minerals, extraction methods, safety protocols, and the importance of sustainable resource management.',
    missions: [
      { id: 1, name: 'Mineral Match', description: 'Match minerals to their properties', type: 'match', stars: 1,
        items: [
          { id: 'mm1', left: 'Diamond', right: 'Hardest natural mineral', match: 'mm1' },
          { id: 'mm2', left: 'Coal', right: 'Fossil fuel, carbon-based', match: 'mm2' },
          { id: 'mm3', left: 'Iron Ore', right: 'Used to make steel', match: 'mm3' },
          { id: 'mm4', left: 'Bauxite', right: 'Source of aluminum', match: 'mm4' },
          { id: 'mm5', left: 'Mica', right: 'Electrical insulator', match: 'mm5' },
        ]
      },
      { id: 2, name: 'Mining Types', description: 'Categorize mining methods', type: 'categorize', stars: 1,
        categories: [
          { id: 'surface', label: 'Surface Mining', color: '#f59e0b' },
          { id: 'underground', label: 'Underground Mining', color: '#64748b' },
        ],
        items: [
          { id: 'mt1', text: 'Open-pit mining', category: 'surface' }, { id: 'mt2', text: 'Shaft mining', category: 'underground' },
          { id: 'mt3', text: 'Strip mining', category: 'surface' }, { id: 'mt4', text: 'Room and pillar', category: 'underground' },
          { id: 'mt5', text: 'Quarrying', category: 'surface' }, { id: 'mt6', text: 'Longwall mining', category: 'underground' },
        ]
      },
      { id: 3, name: 'Mining Process', description: 'Order the mining workflow', type: 'order', stars: 2,
        items: [
          { id: 'mp1', text: 'Geological survey', order: 1 }, { id: 'mp2', text: 'Site preparation', order: 2 },
          { id: 'mp3', text: 'Extraction/drilling', order: 3 }, { id: 'mp4', text: 'Ore processing', order: 4 },
          { id: 'mp5', text: 'Refining', order: 5 }, { id: 'mp6', text: 'Land rehabilitation', order: 6 },
        ]
      },
      { id: 4, name: 'Mining Quiz', description: 'Test your mining knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'India is the largest producer of?', options: ['Gold', 'Mica', 'Diamond', 'Copper'], answer: 'Mica' },
          { question: 'Jharkhand is known for?', options: ['Oil', 'Coal & minerals', 'Gold', 'Uranium only'], answer: 'Coal & minerals' },
          { question: 'What is smelting?', options: ['Cutting rocks', 'Extracting metal from ore', 'Drilling holes', 'Washing minerals'], answer: 'Extracting metal from ore' },
        ]
      },
      { id: 5, name: 'Mining Fill-Ins', description: 'Complete mining concepts', type: 'fill-blank', stars: 2,
        items: [
          { id: 'mfb1', sentence: '___ mining involves removing surface layers to access minerals.', answer: 'Open-pit' },
          { id: 'mfb2', sentence: 'Bauxite is the primary ore of ___.', answer: 'aluminum' },
          { id: 'mfb3', sentence: 'The process of heating ore to extract metal is called ___.', answer: 'smelting' },
          { id: 'mfb4', sentence: 'Land ___ restores mined land to its natural state.', answer: 'rehabilitation' },
        ],
        distractors: ['iron', 'drilling', 'Strip']
      },
      { id: 6, name: 'Mining Fact Check', description: 'True or False about mining', type: 'true-false', stars: 3,
        statements: [
          { id: 'mtf1', text: 'Coal is a renewable resource.', answer: false },
          { id: 'mtf2', text: 'India is one of the largest producers of iron ore.', answer: true },
          { id: 'mtf3', text: 'Diamond is the hardest known natural material.', answer: true },
          { id: 'mtf4', text: 'All mining operations are done underground.', answer: false },
          { id: 'mtf5', text: 'Petroleum is sometimes called "black gold".', answer: true },
          { id: 'mtf6', text: 'Quarrying is a type of underground mining.', answer: false },
        ]
      },
      { id: 7, name: 'Mining Safety', description: 'Order mine safety procedures', type: 'order', stars: 2,
        items: [
          { id: 'msa1', text: 'Conduct safety briefing', order: 1 }, { id: 'msa2', text: 'Wear protective equipment', order: 2 },
          { id: 'msa3', text: 'Check ventilation systems', order: 3 }, { id: 'msa4', text: 'Test for gas leaks', order: 4 },
          { id: 'msa5', text: 'Begin mining operation', order: 5 }, { id: 'msa6', text: 'Regular safety inspections during work', order: 6 },
        ]
      },
      { id: 8, name: 'Indian Mining States', description: 'Match states to their key minerals', type: 'match', stars: 2,
        items: [
          { id: 'mis1', left: 'Jharkhand', right: 'Coal & Iron Ore', match: 'mis1' },
          { id: 'mis2', left: 'Rajasthan', right: 'Marble & Zinc', match: 'mis2' },
          { id: 'mis3', left: 'Odisha', right: 'Chromite & Bauxite', match: 'mis3' },
          { id: 'mis4', left: 'Karnataka', right: 'Gold & Manganese', match: 'mis4' },
          { id: 'mis5', left: 'Assam', right: 'Petroleum & Natural Gas', match: 'mis5' },
        ]
      },
      { id: 9, name: 'Renewable vs Non-Renewable', description: 'Classify resources', type: 'categorize', stars: 2,
        categories: [
          { id: 'renewable', label: 'Renewable', color: '#22c55e' },
          { id: 'nonrenewable', label: 'Non-Renewable', color: '#ef4444' },
        ],
        items: [
          { id: 'mrn1', text: 'Solar energy', category: 'renewable' }, { id: 'mrn2', text: 'Coal', category: 'nonrenewable' },
          { id: 'mrn3', text: 'Wind energy', category: 'renewable' }, { id: 'mrn4', text: 'Natural gas', category: 'nonrenewable' },
          { id: 'mrn5', text: 'Biomass', category: 'renewable' }, { id: 'mrn6', text: 'Petroleum', category: 'nonrenewable' },
          { id: 'mrn7', text: 'Hydropower', category: 'renewable' }, { id: 'mrn8', text: 'Uranium', category: 'nonrenewable' },
        ]
      },
      { id: 10, name: 'Mining Impact Quiz', description: 'Environmental impact of mining', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is acid mine drainage?', options: ['Clean water from mines', 'Acidic water from mineral exposure', 'Drainage system in mines', 'Water used for processing'], answer: 'Acidic water from mineral exposure' },
          { question: 'Reclamation in mining means?', options: ['Finding new minerals', 'Restoring mined land', 'Selling minerals', 'Building roads'], answer: 'Restoring mined land' },
          { question: 'What is the major environmental concern of coal mining?', options: ['Noise', 'Air pollution & greenhouse gases', 'Light pollution', 'None'], answer: 'Air pollution & greenhouse gases' },
          { question: 'Which Indian body regulates mines?', options: ['SEBI', 'Indian Bureau of Mines', 'ISRO', 'RBI'], answer: 'Indian Bureau of Mines' },
          { question: 'Sustainable mining focuses on?', options: ['Maximum extraction', 'Environment + economy balance', 'Speed of extraction', 'Export only'], answer: 'Environment + economy balance' },
        ]
      },
    ]
  },

  metallurgy: {
    title: 'Metallurgy Lab',
    icon: Mountain,
    color: '#71717a',
    info: 'Study metals and alloys — learn about metal properties, extraction processes, alloy compositions, and industrial applications.',
    missions: [
      { id: 1, name: 'Metal Properties', description: 'Match metals to their key properties', type: 'match', stars: 1,
        items: [
          { id: 'mp1', left: 'Iron', right: 'Magnetic, strong', match: 'mp1' },
          { id: 'mp2', left: 'Copper', right: 'Excellent conductor', match: 'mp2' },
          { id: 'mp3', left: 'Gold', right: 'Most malleable', match: 'mp3' },
          { id: 'mp4', left: 'Aluminum', right: 'Lightweight, corrosion resistant', match: 'mp4' },
          { id: 'mp5', left: 'Tungsten', right: 'Highest melting point', match: 'mp5' },
        ]
      },
      { id: 2, name: 'Ferrous vs Non-Ferrous', description: 'Classify metals correctly', type: 'categorize', stars: 1,
        categories: [
          { id: 'ferrous', label: 'Ferrous (Iron-based)', color: '#64748b' },
          { id: 'nonferrous', label: 'Non-Ferrous', color: '#f59e0b' },
        ],
        items: [
          { id: 'mf1', text: 'Steel', category: 'ferrous' }, { id: 'mf2', text: 'Copper', category: 'nonferrous' },
          { id: 'mf3', text: 'Cast Iron', category: 'ferrous' }, { id: 'mf4', text: 'Aluminum', category: 'nonferrous' },
          { id: 'mf5', text: 'Stainless Steel', category: 'ferrous' }, { id: 'mf6', text: 'Zinc', category: 'nonferrous' },
        ]
      },
      { id: 3, name: 'Alloys Match', description: 'Match alloys to their components', type: 'match', stars: 2,
        items: [
          { id: 'al1', left: 'Bronze', right: 'Copper + Tin', match: 'al1' },
          { id: 'al2', left: 'Brass', right: 'Copper + Zinc', match: 'al2' },
          { id: 'al3', left: 'Steel', right: 'Iron + Carbon', match: 'al3' },
          { id: 'al4', left: 'Solder', right: 'Tin + Lead', match: 'al4' },
        ]
      },
      { id: 4, name: 'Metallurgy Quiz', description: 'Test your knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'What is an alloy?', options: ['Pure metal', 'Mixture of metals', 'Type of ore', 'Metal oxide'], answer: 'Mixture of metals' },
          { question: 'Rusting is a process of?', options: ['Reduction', 'Oxidation', 'Melting', 'Cooling'], answer: 'Oxidation' },
          { question: 'Which metal is liquid at room temperature?', options: ['Lead', 'Mercury', 'Tin', 'Zinc'], answer: 'Mercury' },
        ]
      },
      { id: 5, name: 'Metal Concepts', description: 'Fill in the metallurgy blanks', type: 'fill-blank', stars: 2,
        items: [
          { id: 'mefb1', sentence: 'An ___ is a mixture of two or more metals.', answer: 'alloy' },
          { id: 'mefb2', sentence: 'The process of iron reacting with oxygen and moisture is called ___.', answer: 'rusting' },
          { id: 'mefb3', sentence: '___ is the only metal that is liquid at room temperature.', answer: 'Mercury' },
          { id: 'mefb4', sentence: 'Stainless steel contains iron, carbon, and ___.', answer: 'chromium' },
        ],
        distractors: ['melting', 'nickel', 'ore']
      },
      { id: 6, name: 'Metal Facts', description: 'True or False about metals', type: 'true-false', stars: 3,
        statements: [
          { id: 'metf1', text: 'Gold is the best conductor of electricity.', answer: false },
          { id: 'metf2', text: 'Steel is an alloy of iron and carbon.', answer: true },
          { id: 'metf3', text: 'Aluminum is heavier than iron.', answer: false },
          { id: 'metf4', text: 'Copper turns green over time due to oxidation.', answer: true },
          { id: 'metf5', text: 'Brass is made from copper and tin.', answer: false },
          { id: 'metf6', text: 'Titanium is used in aerospace due to its strength-to-weight ratio.', answer: true },
        ]
      },
      { id: 7, name: 'Heat Treatment Order', description: 'Order the metal heat treatment process', type: 'order', stars: 2,
        items: [
          { id: 'mht1', text: 'Select the metal workpiece', order: 1 }, { id: 'mht2', text: 'Heat to required temperature', order: 2 },
          { id: 'mht3', text: 'Hold at temperature (soaking)', order: 3 }, { id: 'mht4', text: 'Cool at controlled rate', order: 4 },
          { id: 'mht5', text: 'Inspect for desired properties', order: 5 }, { id: 'mht6', text: 'Temper if needed', order: 6 },
        ]
      },
      { id: 8, name: 'Metal Applications', description: 'Match metals to their primary use', type: 'match', stars: 2,
        items: [
          { id: 'mapp1', left: 'Copper', right: 'Electrical wiring', match: 'mapp1' },
          { id: 'mapp2', left: 'Aluminum', right: 'Aircraft bodies', match: 'mapp2' },
          { id: 'mapp3', left: 'Lead', right: 'Radiation shielding', match: 'mapp3' },
          { id: 'mapp4', left: 'Zinc', right: 'Galvanizing (rust prevention)', match: 'mapp4' },
          { id: 'mapp5', left: 'Platinum', right: 'Catalytic converters', match: 'mapp5' },
        ]
      },
      { id: 9, name: 'Metal Processing', description: 'Categorize metal working methods', type: 'categorize', stars: 2,
        categories: [
          { id: 'hot', label: 'Hot Working', color: '#ef4444' },
          { id: 'cold', label: 'Cold Working', color: '#3b82f6' },
        ],
        items: [
          { id: 'mpw1', text: 'Forging', category: 'hot' }, { id: 'mpw2', text: 'Cold rolling', category: 'cold' },
          { id: 'mpw3', text: 'Hot rolling', category: 'hot' }, { id: 'mpw4', text: 'Stamping', category: 'cold' },
          { id: 'mpw5', text: 'Casting', category: 'hot' }, { id: 'mpw6', text: 'Wire drawing', category: 'cold' },
          { id: 'mpw7', text: 'Welding', category: 'hot' }, { id: 'mpw8', text: 'Bending', category: 'cold' },
        ]
      },
      { id: 10, name: 'Corrosion & Protection Quiz', description: 'Test corrosion knowledge', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is galvanization?', options: ['Painting metal', 'Coating with zinc', 'Melting metal', 'Cutting metal'], answer: 'Coating with zinc' },
          { question: 'Stainless steel resists rust because of?', options: ['Iron', 'Carbon', 'Chromium', 'Copper'], answer: 'Chromium' },
          { question: 'Anodizing is commonly done on?', options: ['Steel', 'Iron', 'Aluminum', 'Lead'], answer: 'Aluminum' },
          { question: 'What accelerates corrosion?', options: ['Dry air', 'Moisture & salt', 'Cold temperature', 'Darkness'], answer: 'Moisture & salt' },
          { question: 'The process of coating iron with tin is called?', options: ['Galvanizing', 'Tinning', 'Anodizing', 'Electroplating'], answer: 'Tinning' },
        ]
      },
    ]
  },

  microbiology: {
    title: 'Microbiology Explorer',
    icon: Microscope,
    color: '#ec4899',
    info: 'Explore the invisible world of microorganisms — classify bacteria, viruses, and fungi, understand cell structure, and learn about diseases.',
    missions: [
      { id: 1, name: 'Classify Organisms', description: 'Sort microorganisms by type', type: 'categorize', stars: 1,
        categories: [
          { id: 'bacteria', label: 'Bacteria', color: '#22c55e' },
          { id: 'virus', label: 'Virus', color: '#ef4444' },
          { id: 'fungi', label: 'Fungi', color: '#8b5cf6' },
        ],
        items: [
          { id: 'mo1', text: 'E. coli', category: 'bacteria' }, { id: 'mo2', text: 'COVID-19', category: 'virus' },
          { id: 'mo3', text: 'Yeast', category: 'fungi' }, { id: 'mo4', text: 'Salmonella', category: 'bacteria' },
          { id: 'mo5', text: 'Influenza', category: 'virus' }, { id: 'mo6', text: 'Penicillium', category: 'fungi' },
          { id: 'mo7', text: 'Streptococcus', category: 'bacteria' }, { id: 'mo8', text: 'HIV', category: 'virus' },
          { id: 'mo9', text: 'Mushroom', category: 'fungi' },
        ]
      },
      { id: 2, name: 'Disease Match', description: 'Match diseases to their causative organisms', type: 'match', stars: 2,
        items: [
          { id: 'dm1', left: 'Tuberculosis', right: 'Mycobacterium', match: 'dm1' },
          { id: 'dm2', left: 'Malaria', right: 'Plasmodium', match: 'dm2' },
          { id: 'dm3', left: 'Ringworm', right: 'Dermatophytes', match: 'dm3' },
          { id: 'dm4', left: 'Rabies', right: 'Lyssavirus', match: 'dm4' },
          { id: 'dm5', left: 'Cholera', right: 'Vibrio cholerae', match: 'dm5' },
        ]
      },
      { id: 3, name: 'Cell Parts', description: 'Match organelles to functions', type: 'match', stars: 2,
        items: [
          { id: 'cp1', left: 'Nucleus', right: 'Contains DNA', match: 'cp1' },
          { id: 'cp2', left: 'Mitochondria', right: 'Powerhouse of cell', match: 'cp2' },
          { id: 'cp3', left: 'Ribosome', right: 'Protein synthesis', match: 'cp3' },
          { id: 'cp4', left: 'Cell Membrane', right: 'Controls entry/exit', match: 'cp4' },
          { id: 'cp5', left: 'Chloroplast', right: 'Photosynthesis', match: 'cp5' },
        ]
      },
      { id: 4, name: 'Microbiology Quiz', description: 'Test your knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'Who is the father of microbiology?', options: ['Pasteur', 'Leeuwenhoek', 'Koch', 'Fleming'], answer: 'Leeuwenhoek' },
          { question: 'Antibiotics work against?', options: ['Viruses', 'Bacteria', 'Both', 'Neither'], answer: 'Bacteria' },
          { question: 'Penicillin was discovered by?', options: ['Pasteur', 'Koch', 'Fleming', 'Jenner'], answer: 'Fleming' },
          { question: 'Vaccine for smallpox was by?', options: ['Pasteur', 'Jenner', 'Koch', 'Salk'], answer: 'Jenner' },
        ]
      },
      { id: 5, name: 'Cell Biology Fill-In', description: 'Complete the biology concepts', type: 'fill-blank', stars: 3,
        instruction: 'Complete each statement about microbiology:',
        items: [
          { id: 'mbfb1', sentence: 'The ___ is the powerhouse of the cell.', answer: 'mitochondria' },
          { id: 'mbfb2', sentence: 'DNA stands for Deoxyribonucleic ___.', answer: 'Acid' },
          { id: 'mbfb3', sentence: '___ discovered penicillin, the first antibiotic.', answer: 'Fleming' },
          { id: 'mbfb4', sentence: 'Bacteria reproduce by ___ fission.', answer: 'binary' },
          { id: 'mbfb5', sentence: 'A ___ is used to observe microorganisms.', answer: 'microscope' },
        ],
        distractors: ['telescope', 'Pasteur', 'cell']
      },
      { id: 6, name: 'Micro Facts Challenge', description: 'Rapid fire biology facts', type: 'true-false', stars: 3,
        statements: [
          { id: 'mbtf1', text: 'Viruses can reproduce on their own without a host.', answer: false },
          { id: 'mbtf2', text: 'Bacteria are larger than viruses.', answer: true },
          { id: 'mbtf3', text: 'Antibiotics are effective against viral infections.', answer: false },
          { id: 'mbtf4', text: 'Yeast is a type of fungus used in bread making.', answer: true },
          { id: 'mbtf5', text: 'All bacteria are harmful to humans.', answer: false },
          { id: 'mbtf6', text: 'Louis Pasteur developed the process of pasteurization.', answer: true },
          { id: 'mbtf7', text: 'Red blood cells contain a nucleus.', answer: false },
          { id: 'mbtf8', text: 'The human body contains more bacterial cells than human cells.', answer: true },
        ]
      },
      { id: 7, name: 'Lab Equipment', description: 'Match lab equipment to their uses', type: 'match', stars: 2,
        items: [
          { id: 'mle1', left: 'Microscope', right: 'Magnify tiny organisms', match: 'mle1' },
          { id: 'mle2', left: 'Petri Dish', right: 'Culture bacteria', match: 'mle2' },
          { id: 'mle3', left: 'Autoclave', right: 'Sterilize equipment', match: 'mle3' },
          { id: 'mle4', left: 'Centrifuge', right: 'Separate components by spinning', match: 'mle4' },
          { id: 'mle5', left: 'Incubator', right: 'Maintain growth temperature', match: 'mle5' },
        ]
      },
      { id: 8, name: 'Immune Response', description: 'Order the immune response steps', type: 'order', stars: 2,
        items: [
          { id: 'mir1', text: 'Pathogen enters the body', order: 1 }, { id: 'mir2', text: 'White blood cells detect invader', order: 2 },
          { id: 'mir3', text: 'Phagocytes engulf the pathogen', order: 3 }, { id: 'mir4', text: 'T-cells and B-cells activated', order: 4 },
          { id: 'mir5', text: 'Antibodies produced', order: 5 }, { id: 'mir6', text: 'Memory cells formed for future protection', order: 6 },
        ]
      },
      { id: 9, name: 'Beneficial vs Harmful', description: 'Categorize microorganisms', type: 'categorize', stars: 2,
        categories: [
          { id: 'helpful', label: 'Beneficial', color: '#22c55e' },
          { id: 'harmful', label: 'Harmful', color: '#ef4444' },
        ],
        items: [
          { id: 'mbh1', text: 'Lactobacillus (yogurt)', category: 'helpful' }, { id: 'mbh2', text: 'Salmonella', category: 'harmful' },
          { id: 'mbh3', text: 'Yeast (bread)', category: 'helpful' }, { id: 'mbh4', text: 'E. coli O157', category: 'harmful' },
          { id: 'mbh5', text: 'Penicillium (antibiotic)', category: 'helpful' }, { id: 'mbh6', text: 'Plasmodium (malaria)', category: 'harmful' },
          { id: 'mbh7', text: 'Rhizobium (soil fertility)', category: 'helpful' }, { id: 'mbh8', text: 'Staphylococcus aureus', category: 'harmful' },
        ]
      },
      { id: 10, name: 'Vaccination Quiz', description: 'Test vaccination knowledge', type: 'quiz', stars: 3,
        questions: [
          { question: 'What does a vaccine contain?', options: ['Active virus', 'Weakened/dead pathogen', 'Antibiotics', 'Vitamins'], answer: 'Weakened/dead pathogen' },
          { question: 'BCG vaccine protects against?', options: ['Polio', 'Tuberculosis', 'Measles', 'Hepatitis'], answer: 'Tuberculosis' },
          { question: 'Herd immunity means?', options: ['Only animals are immune', 'Enough people vaccinated to protect all', 'No one is immune', 'Only children immune'], answer: 'Enough people vaccinated to protect all' },
          { question: 'Who developed the first vaccine?', options: ['Pasteur', 'Jenner', 'Koch', 'Fleming'], answer: 'Jenner' },
          { question: 'OPV stands for?', options: ['Oral Polio Vaccine', 'Original Pathogen Virus', 'Organic Protection Vaccine', 'Oral Prevention Virus'], answer: 'Oral Polio Vaccine' },
        ]
      },
    ]
  },

  'road-safety': {
    title: 'Road Safety Lab',
    icon: ShieldCheck,
    color: '#dc2626',
    info: 'Learn essential road safety rules, traffic signs, pedestrian safety, and emergency procedures to stay safe on Indian roads.',
    missions: [
      { id: 1, name: 'Traffic Signs', description: 'Match traffic signs to their meanings', type: 'match', stars: 1,
        items: [
          { id: 'rs1', left: 'Red Circle', right: 'Prohibitory (No entry)', match: 'rs1' },
          { id: 'rs2', left: 'Yellow Triangle', right: 'Warning/Caution', match: 'rs2' },
          { id: 'rs3', left: 'Blue Circle', right: 'Mandatory', match: 'rs3' },
          { id: 'rs4', left: 'Green Rectangle', right: 'Information', match: 'rs4' },
          { id: 'rs5', left: 'Red Octagon', right: 'Stop sign', match: 'rs5' },
        ]
      },
      { id: 2, name: 'Safe vs Unsafe', description: 'Categorize road behaviors', type: 'categorize', stars: 1,
        categories: [
          { id: 'safe', label: 'Safe Behavior', color: '#22c55e' },
          { id: 'unsafe', label: 'Unsafe Behavior', color: '#ef4444' },
        ],
        items: [
          { id: 'sb1', text: 'Wearing seatbelt', category: 'safe' }, { id: 'sb2', text: 'Using phone while driving', category: 'unsafe' },
          { id: 'sb3', text: 'Using zebra crossing', category: 'safe' }, { id: 'sb4', text: 'Jumping red light', category: 'unsafe' },
          { id: 'sb5', text: 'Wearing helmet', category: 'safe' }, { id: 'sb6', text: 'Drunk driving', category: 'unsafe' },
          { id: 'sb7', text: 'Using indicators', category: 'safe' }, { id: 'sb8', text: 'Wrong-side driving', category: 'unsafe' },
        ]
      },
      { id: 3, name: 'Accident Response', description: 'Order the steps after an accident', type: 'order', stars: 2,
        items: [
          { id: 'ar1', text: 'Stay calm and check for injuries', order: 1 }, { id: 'ar2', text: 'Call emergency services (112)', order: 2 },
          { id: 'ar3', text: 'Move to safe location if possible', order: 3 }, { id: 'ar4', text: 'Provide first aid if trained', order: 4 },
          { id: 'ar5', text: 'Document the scene', order: 5 }, { id: 'ar6', text: 'Exchange information', order: 6 },
        ]
      },
      { id: 4, name: 'Road Safety Quiz', description: 'Test your knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'Minimum age for driving in India (with license)?', options: ['16', '18', '21', '14'], answer: '18' },
          { question: 'Emergency number in India?', options: ['911', '112', '100', '108'], answer: '112' },
          { question: 'Pedestrians should walk on which side?', options: ['Left', 'Right', 'Middle', 'Any side'], answer: 'Right' },
          { question: 'Yellow traffic light means?', options: ['Go fast', 'Stop', 'Slow down/prepare to stop', 'Reverse'], answer: 'Slow down/prepare to stop' },
        ]
      },
      { id: 5, name: 'Road Rules Fill-In', description: 'Complete road safety rules', type: 'fill-blank', stars: 2,
        items: [
          { id: 'rsfb1', sentence: 'The emergency helpline number in India is ___.', answer: '112' },
          { id: 'rsfb2', sentence: 'A ___ crossing is the safest place for pedestrians to cross.', answer: 'zebra' },
          { id: 'rsfb3', sentence: 'The minimum age for a driving license in India is ___ years.', answer: '18' },
          { id: 'rsfb4', sentence: 'A red traffic light means you must ___.', answer: 'stop' },
        ],
        distractors: ['16', 'go', 'speed']
      },
      { id: 6, name: 'Road Safety Rapid Fire', description: 'True or False road facts', type: 'true-false', stars: 3,
        statements: [
          { id: 'rstf1', text: 'Wearing a seatbelt is optional for back-seat passengers in India.', answer: false },
          { id: 'rstf2', text: 'Two-wheeler riders must wear helmets as per Indian law.', answer: true },
          { id: 'rstf3', text: 'You can use mobile phones while driving if using hands-free.', answer: false },
          { id: 'rstf4', text: 'Speed limits are different in school zones.', answer: true },
          { id: 'rstf5', text: 'Honking is banned in hospital and school zones.', answer: true },
          { id: 'rstf6', text: 'Overtaking from the left side is legal in India.', answer: false },
        ]
      },
      { id: 7, name: 'Vehicle Check Steps', description: 'Order pre-drive safety checks', type: 'order', stars: 2,
        items: [
          { id: 'rvs1', text: 'Check tyre pressure and condition', order: 1 }, { id: 'rvs2', text: 'Adjust mirrors', order: 2 },
          { id: 'rvs3', text: 'Fasten seatbelt', order: 3 }, { id: 'rvs4', text: 'Check fuel level', order: 4 },
          { id: 'rvs5', text: 'Test brakes', order: 5 }, { id: 'rvs6', text: 'Start the engine', order: 6 },
        ]
      },
      { id: 8, name: 'Road Hazards', description: 'Categorize road hazards by type', type: 'categorize', stars: 2,
        categories: [
          { id: 'natural', label: 'Natural Hazards', color: '#f59e0b' },
          { id: 'manmade', label: 'Human-Made Hazards', color: '#ef4444' },
        ],
        items: [
          { id: 'rrh1', text: 'Potholes', category: 'manmade' }, { id: 'rrh2', text: 'Fog', category: 'natural' },
          { id: 'rrh3', text: 'Broken traffic lights', category: 'manmade' }, { id: 'rrh4', text: 'Heavy rain', category: 'natural' },
          { id: 'rrh5', text: 'Missing road signs', category: 'manmade' }, { id: 'rrh6', text: 'Landslide debris', category: 'natural' },
          { id: 'rrh7', text: 'Construction zones', category: 'manmade' }, { id: 'rrh8', text: 'Black ice', category: 'natural' },
        ]
      },
      { id: 9, name: 'Pedestrian Safety', description: 'Match pedestrian rules', type: 'match', stars: 2,
        items: [
          { id: 'rps1', left: 'Zebra Crossing', right: 'Safest place to cross road', match: 'rps1' },
          { id: 'rps2', left: 'Footpath', right: 'Designated walking area', match: 'rps2' },
          { id: 'rps3', left: 'Pedestrian Signal', right: 'Green man = safe to cross', match: 'rps3' },
          { id: 'rps4', left: 'Reflective clothing', right: 'Be visible at night', match: 'rps4' },
          { id: 'rps5', left: 'Subway/Overpass', right: 'Safe crossing over busy roads', match: 'rps5' },
        ]
      },
      { id: 10, name: 'Advanced Road Safety Quiz', description: 'Advanced road safety test', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is the speed limit in residential areas in India?', options: ['20 km/h', '25 km/h', '30 km/h', '50 km/h'], answer: '25 km/h' },
          { question: 'DUI stands for?', options: ['Driving Under Influence', 'Driving Unsafe Instructions', 'Driver Uniform Inspection', 'Drive Until Intersection'], answer: 'Driving Under Influence' },
          { question: 'Which document is NOT needed while driving?', options: ['License', 'Registration', 'Insurance', 'Passport'], answer: 'Passport' },
          { question: 'In India, we drive on which side?', options: ['Right', 'Left', 'Center', 'Any side'], answer: 'Left' },
          { question: 'What does a flashing red light mean?', options: ['Speed up', 'Stop then proceed if safe', 'Lane closed', 'No parking'], answer: 'Stop then proceed if safe' },
        ]
      },
    ]
  },

  'civic-manners': {
    title: 'Civic Manners Lab',
    icon: Heart,
    color: '#d946ef',
    info: 'Learn civic responsibility, good manners, democratic values, and how to be a responsible citizen in society.',
    missions: [
      { id: 1, name: 'Good vs Bad Manners', description: 'Categorize behaviors', type: 'categorize', stars: 1,
        categories: [
          { id: 'good', label: 'Good Manners', color: '#22c55e' },
          { id: 'bad', label: 'Bad Manners', color: '#ef4444' },
        ],
        items: [
          { id: 'cm1', text: 'Saying "Thank you"', category: 'good' }, { id: 'cm2', text: 'Littering', category: 'bad' },
          { id: 'cm3', text: 'Queueing patiently', category: 'good' }, { id: 'cm4', text: 'Talking loudly in library', category: 'bad' },
          { id: 'cm5', text: 'Respecting elders', category: 'good' }, { id: 'cm6', text: 'Cutting in line', category: 'bad' },
          { id: 'cm7', text: 'Helping the needy', category: 'good' }, { id: 'cm8', text: 'Vandalizing property', category: 'bad' },
        ]
      },
      { id: 2, name: 'Fundamental Rights', description: 'Match rights to descriptions', type: 'match', stars: 2,
        items: [
          { id: 'fr1', left: 'Right to Equality', right: 'No discrimination by state', match: 'fr1' },
          { id: 'fr2', left: 'Right to Freedom', right: 'Speech, assembly, movement', match: 'fr2' },
          { id: 'fr3', left: 'Right to Education', right: 'Free education 6-14 years', match: 'fr3' },
          { id: 'fr4', left: 'Right against Exploitation', right: 'No child labor, forced labor', match: 'fr4' },
        ]
      },
      { id: 3, name: 'Voting Process', description: 'Order the steps of voting', type: 'order', stars: 1,
        items: [
          { id: 'vp1', text: 'Register as a voter', order: 1 }, { id: 'vp2', text: 'Check your name on voter list', order: 2 },
          { id: 'vp3', text: 'Go to polling booth on election day', order: 3 }, { id: 'vp4', text: 'Show voter ID', order: 4 },
          { id: 'vp5', text: 'Cast your vote using EVM', order: 5 }, { id: 'vp6', text: 'Get ink mark on finger', order: 6 },
        ]
      },
      { id: 4, name: 'Civics Quiz', description: 'Test your civic knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'Minimum voting age in India?', options: ['16', '18', '21', '25'], answer: '18' },
          { question: 'Who is the head of state in India?', options: ['PM', 'President', 'Governor', 'Chief Justice'], answer: 'President' },
          { question: 'India became a Republic on?', options: ['15 Aug 1947', '26 Jan 1950', '2 Oct 1947', '26 Nov 1949'], answer: '26 Jan 1950' },
        ]
      },
      { id: 5, name: 'Civic Fill-In', description: 'Complete civic concepts', type: 'fill-blank', stars: 2,
        items: [
          { id: 'cfb1', sentence: 'The Indian Constitution was adopted on ___ January 1950.', answer: '26' },
          { id: 'cfb2', sentence: 'The right to ___ is a fundamental right for children aged 6-14.', answer: 'education' },
          { id: 'cfb3', sentence: 'India is a ___ republic with an elected head of state.', answer: 'democratic' },
          { id: 'cfb4', sentence: 'An EVM is an Electronic ___ Machine.', answer: 'Voting' },
        ],
        distractors: ['15', 'work', 'Counting']
      },
      { id: 6, name: 'Civic Facts', description: 'True or False about citizenship', type: 'true-false', stars: 3,
        statements: [
          { id: 'ctf1', text: 'Voting is a fundamental right in India.', answer: true },
          { id: 'ctf2', text: 'The Indian Constitution has only 5 fundamental rights.', answer: false },
          { id: 'ctf3', text: 'Littering in public is a punishable offense in India.', answer: true },
          { id: 'ctf4', text: 'Children below 18 can vote in India.', answer: false },
          { id: 'ctf5', text: 'The national anthem of India is "Vande Mataram".', answer: false },
          { id: 'ctf6', text: 'India has a parliamentary form of government.', answer: true },
        ]
      },
      { id: 7, name: 'Public Service Match', description: 'Match public services to their role', type: 'match', stars: 2,
        items: [
          { id: 'cps1', left: 'Police', right: 'Maintain law and order', match: 'cps1' },
          { id: 'cps2', left: 'Fire Department', right: 'Fight fires and rescue', match: 'cps2' },
          { id: 'cps3', left: 'Municipal Corporation', right: 'City sanitation and roads', match: 'cps3' },
          { id: 'cps4', left: 'Post Office', right: 'Mail and financial services', match: 'cps4' },
          { id: 'cps5', left: 'Public Library', right: 'Free access to books and information', match: 'cps5' },
        ]
      },
      { id: 8, name: 'Cleanliness Drive', description: 'Order steps for a community cleanup', type: 'order', stars: 1,
        items: [
          { id: 'ccd1', text: 'Identify the area to clean', order: 1 }, { id: 'ccd2', text: 'Gather volunteers', order: 2 },
          { id: 'ccd3', text: 'Collect cleaning supplies', order: 3 }, { id: 'ccd4', text: 'Clean and collect waste', order: 4 },
          { id: 'ccd5', text: 'Segregate waste into categories', order: 5 }, { id: 'ccd6', text: 'Dispose responsibly', order: 6 },
        ]
      },
      { id: 9, name: 'Waste Management', description: 'Categorize waste types', type: 'categorize', stars: 2,
        categories: [
          { id: 'wet', label: 'Wet/Organic Waste', color: '#22c55e' },
          { id: 'dry', label: 'Dry/Recyclable Waste', color: '#3b82f6' },
          { id: 'hazard', label: 'Hazardous Waste', color: '#ef4444' },
        ],
        items: [
          { id: 'cwm1', text: 'Food scraps', category: 'wet' }, { id: 'cwm2', text: 'Plastic bottles', category: 'dry' },
          { id: 'cwm3', text: 'Batteries', category: 'hazard' }, { id: 'cwm4', text: 'Fruit peels', category: 'wet' },
          { id: 'cwm5', text: 'Paper and cardboard', category: 'dry' }, { id: 'cwm6', text: 'Medical waste', category: 'hazard' },
          { id: 'cwm7', text: 'Garden leaves', category: 'wet' }, { id: 'cwm8', text: 'Glass jars', category: 'dry' },
          { id: 'cwm9', text: 'Paint cans', category: 'hazard' },
        ]
      },
      { id: 10, name: 'National Symbols Quiz', description: 'Test knowledge of Indian national symbols', type: 'quiz', stars: 2,
        questions: [
          { question: 'National bird of India?', options: ['Sparrow', 'Peacock', 'Eagle', 'Parrot'], answer: 'Peacock' },
          { question: 'National flower of India?', options: ['Rose', 'Lotus', 'Jasmine', 'Sunflower'], answer: 'Lotus' },
          { question: 'National animal of India?', options: ['Lion', 'Elephant', 'Bengal Tiger', 'Leopard'], answer: 'Bengal Tiger' },
          { question: 'National river of India?', options: ['Yamuna', 'Ganga', 'Narmada', 'Godavari'], answer: 'Ganga' },
          { question: 'India Republic Day is on?', options: ['15 August', '26 January', '2 October', '14 November'], answer: '26 January' },
        ]
      },
    ]
  },

  'life-skills': {
    title: 'Life Skills Lab',
    icon: Lightbulb,
    color: '#65a30d',
    info: 'Build essential life skills — communication, decision-making, teamwork, empathy, time management, and emotional intelligence.',
    missions: [
      { id: 1, name: 'Communication Types', description: 'Categorize communication methods', type: 'categorize', stars: 1,
        categories: [
          { id: 'verbal', label: 'Verbal', color: '#3b82f6' },
          { id: 'nonverbal', label: 'Non-Verbal', color: '#22c55e' },
          { id: 'written', label: 'Written', color: '#f59e0b' },
        ],
        items: [
          { id: 'ls1', text: 'Speech', category: 'verbal' }, { id: 'ls2', text: 'Body language', category: 'nonverbal' },
          { id: 'ls3', text: 'Email', category: 'written' }, { id: 'ls4', text: 'Phone call', category: 'verbal' },
          { id: 'ls5', text: 'Facial expressions', category: 'nonverbal' }, { id: 'ls6', text: 'Letter', category: 'written' },
          { id: 'ls7', text: 'Eye contact', category: 'nonverbal' }, { id: 'ls8', text: 'Report', category: 'written' },
        ]
      },
      { id: 2, name: 'Decision Making', description: 'Order the decision-making process', type: 'order', stars: 2,
        items: [
          { id: 'dm1', text: 'Identify the problem', order: 1 }, { id: 'dm2', text: 'Gather information', order: 2 },
          { id: 'dm3', text: 'List possible options', order: 3 }, { id: 'dm4', text: 'Evaluate pros and cons', order: 4 },
          { id: 'dm5', text: 'Make the decision', order: 5 }, { id: 'dm6', text: 'Review the outcome', order: 6 },
        ]
      },
      { id: 3, name: 'Conflict Resolution', description: 'Match strategies to situations', type: 'match', stars: 2,
        items: [
          { id: 'cr1', left: 'Active Listening', right: 'Pay attention without interrupting', match: 'cr1' },
          { id: 'cr2', left: 'Compromise', right: 'Both sides give a little', match: 'cr2' },
          { id: 'cr3', left: 'Empathy', right: "Understand others' feelings", match: 'cr3' },
          { id: 'cr4', left: 'Assertiveness', right: 'Express needs respectfully', match: 'cr4' },
        ]
      },
      { id: 4, name: 'Life Skills Quiz', description: 'Test your knowledge', type: 'quiz', stars: 1,
        questions: [
          { question: 'Which is a soft skill?', options: ['Coding', 'Communication', 'Mathematics', 'Science'], answer: 'Communication' },
          { question: 'Empathy means?', options: ['Being strong', 'Being smart', "Understanding others' feelings", 'Being brave'], answer: "Understanding others' feelings" },
          { question: 'Time management helps with?', options: ['Only studies', 'Only work', 'All areas of life', 'Only sports'], answer: 'All areas of life' },
        ]
      },
      { id: 5, name: 'Life Skills Fill-In', description: 'Complete the life skills concepts', type: 'fill-blank', stars: 2,
        items: [
          { id: 'lsfb1', sentence: '___ is the ability to understand and share the feelings of another.', answer: 'Empathy' },
          { id: 'lsfb2', sentence: 'Active ___ means fully concentrating on what someone is saying.', answer: 'listening' },
          { id: 'lsfb3', sentence: 'A ___ is someone who guides and mentors others.', answer: 'leader' },
          { id: 'lsfb4', sentence: 'Setting ___ helps you prioritize tasks and manage time.', answer: 'goals' },
        ],
        distractors: ['rules', 'talking', 'Sympathy']
      },
      { id: 6, name: 'Life Skills True/False', description: 'Rapid fire life skills facts', type: 'true-false', stars: 3,
        statements: [
          { id: 'lstf1', text: 'Teamwork requires only technical skills.', answer: false },
          { id: 'lstf2', text: 'Critical thinking involves analyzing information objectively.', answer: true },
          { id: 'lstf3', text: 'Good communication is only about speaking well.', answer: false },
          { id: 'lstf4', text: 'Emotional intelligence helps in managing relationships.', answer: true },
          { id: 'lstf5', text: 'Stress management is not an important life skill.', answer: false },
          { id: 'lstf6', text: 'Being assertive means being aggressive.', answer: false },
        ]
      },
      { id: 7, name: 'Emotions Categorize', description: 'Sort emotions by type', type: 'categorize', stars: 1,
        categories: [
          { id: 'positive', label: 'Positive Emotions', color: '#22c55e' },
          { id: 'negative', label: 'Challenging Emotions', color: '#f59e0b' },
        ],
        items: [
          { id: 'lse1', text: 'Joy', category: 'positive' }, { id: 'lse2', text: 'Anger', category: 'negative' },
          { id: 'lse3', text: 'Gratitude', category: 'positive' }, { id: 'lse4', text: 'Fear', category: 'negative' },
          { id: 'lse5', text: 'Confidence', category: 'positive' }, { id: 'lse6', text: 'Jealousy', category: 'negative' },
          { id: 'lse7', text: 'Compassion', category: 'positive' }, { id: 'lse8', text: 'Frustration', category: 'negative' },
        ]
      },
      { id: 8, name: 'Problem Solving Steps', description: 'Order the problem solving process', type: 'order', stars: 2,
        items: [
          { id: 'lsps1', text: 'Identify the problem clearly', order: 1 }, { id: 'lsps2', text: 'Brainstorm possible solutions', order: 2 },
          { id: 'lsps3', text: 'Evaluate each solution', order: 3 }, { id: 'lsps4', text: 'Choose the best option', order: 4 },
          { id: 'lsps5', text: 'Implement the solution', order: 5 }, { id: 'lsps6', text: 'Review and learn from outcome', order: 6 },
        ]
      },
      { id: 9, name: 'Time Management Match', description: 'Match techniques to descriptions', type: 'match', stars: 2,
        items: [
          { id: 'lstm1', left: 'Eisenhower Matrix', right: 'Prioritize by urgency and importance', match: 'lstm1' },
          { id: 'lstm2', left: 'Pomodoro Technique', right: 'Work in 25-min focused intervals', match: 'lstm2' },
          { id: 'lstm3', left: 'To-Do List', right: 'Write down tasks to complete', match: 'lstm3' },
          { id: 'lstm4', left: 'Time Blocking', right: 'Schedule specific hours for tasks', match: 'lstm4' },
          { id: 'lstm5', left: 'SMART Goals', right: 'Specific, Measurable, Achievable, Relevant, Time-bound', match: 'lstm5' },
        ]
      },
      { id: 10, name: 'Leadership Quiz', description: 'Test your leadership knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'A good leader should?', options: ['Only give orders', 'Listen and inspire', 'Work alone', 'Avoid decisions'], answer: 'Listen and inspire' },
          { question: 'What is delegation?', options: ['Doing everything yourself', 'Assigning tasks to others', 'Ignoring work', 'Complaining'], answer: 'Assigning tasks to others' },
          { question: 'Teamwork means?', options: ['One person does all work', 'Working together toward a goal', 'Competing against teammates', 'Working individually'], answer: 'Working together toward a goal' },
          { question: 'Which is a sign of emotional intelligence?', options: ['Ignoring feelings', 'Understanding others emotions', 'Always being right', 'Avoiding people'], answer: 'Understanding others emotions' },
          { question: 'Resilience means?', options: ['Giving up easily', 'Bouncing back from setbacks', 'Avoiding challenges', 'Being perfect'], answer: 'Bouncing back from setbacks' },
        ]
      },
    ]
  },

  financial: {
    title: 'Financial Planning Lab',
    icon: Wallet,
    color: '#059669',
    info: 'Learn money management, budgeting, saving, banking basics, and smart financial decisions for a secure future.',
    missions: [
      { id: 1, name: 'Needs vs Wants', description: 'Categorize expenses correctly', type: 'categorize', stars: 1,
        categories: [
          { id: 'need', label: 'Needs', color: '#22c55e' },
          { id: 'want', label: 'Wants', color: '#f59e0b' },
        ],
        items: [
          { id: 'fp1', text: 'Food', category: 'need' }, { id: 'fp2', text: 'Latest smartphone', category: 'want' },
          { id: 'fp3', text: 'Rent/Housing', category: 'need' }, { id: 'fp4', text: 'Designer clothes', category: 'want' },
          { id: 'fp5', text: 'Education', category: 'need' }, { id: 'fp6', text: 'Gaming console', category: 'want' },
          { id: 'fp7', text: 'Medicine', category: 'need' }, { id: 'fp8', text: 'Vacation', category: 'want' },
        ]
      },
      { id: 2, name: 'Banking Terms', description: 'Match banking terms to definitions', type: 'match', stars: 1,
        items: [
          { id: 'bt1', left: 'Savings Account', right: 'Earns interest on deposits', match: 'bt1' },
          { id: 'bt2', left: 'Fixed Deposit', right: 'Locked savings for higher interest', match: 'bt2' },
          { id: 'bt3', left: 'EMI', right: 'Monthly loan payment', match: 'bt3' },
          { id: 'bt4', left: 'UPI', right: 'Digital payment system', match: 'bt4' },
          { id: 'bt5', left: 'SIP', right: 'Regular mutual fund investment', match: 'bt5' },
        ]
      },
      { id: 3, name: 'Open a Bank Account', description: 'Order the steps', type: 'order', stars: 2,
        items: [
          { id: 'ba1', text: 'Choose a bank', order: 1 }, { id: 'ba2', text: 'Fill application form', order: 2 },
          { id: 'ba3', text: 'Submit KYC documents', order: 3 }, { id: 'ba4', text: 'Initial deposit', order: 4 },
          { id: 'ba5', text: 'Receive account details', order: 5 }, { id: 'ba6', text: 'Activate internet banking', order: 6 },
        ]
      },
      { id: 4, name: 'Finance Quiz', description: 'Test your financial literacy', type: 'quiz', stars: 2,
        questions: [
          { question: 'What is compound interest?', options: ['Interest on principal only', 'Interest on interest too', 'Fixed rate', 'No interest'], answer: 'Interest on interest too' },
          { question: 'GST stands for?', options: ['General Sales Tax', 'Goods and Services Tax', 'Government Standard Tax', 'Global Service Tax'], answer: 'Goods and Services Tax' },
          { question: 'What is inflation?', options: ['Price decrease', 'Price increase over time', 'Fixed prices', 'Tax increase'], answer: 'Price increase over time' },
          { question: 'PAN card is used for?', options: ['Driving', 'Voting', 'Tax identification', 'Travel'], answer: 'Tax identification' },
        ]
      },
      { id: 5, name: 'Finance Fill-In', description: 'Complete financial concepts', type: 'fill-blank', stars: 2,
        items: [
          { id: 'ffb1', sentence: '___ interest is calculated on both principal and accumulated interest.', answer: 'Compound' },
          { id: 'ffb2', sentence: 'KYC stands for Know Your ___.', answer: 'Customer' },
          { id: 'ffb3', sentence: 'A ___ is a financial plan that tracks income and expenses.', answer: 'budget' },
          { id: 'ffb4', sentence: 'GST is a ___ levied on goods and services in India.', answer: 'tax' },
          { id: 'ffb5', sentence: 'An ___ fund is money saved for unexpected expenses.', answer: 'emergency' },
        ],
        distractors: ['Simple', 'Client', 'fee']
      },
      { id: 6, name: 'Finance Fact Check', description: 'True or False about money', type: 'true-false', stars: 3,
        statements: [
          { id: 'ftf1', text: 'A credit card gives you free money.', answer: false },
          { id: 'ftf2', text: 'Saving 10% of income is a good financial habit.', answer: true },
          { id: 'ftf3', text: 'Inflation reduces the purchasing power of money.', answer: true },
          { id: 'ftf4', text: 'FD (Fixed Deposit) can be withdrawn anytime without penalty.', answer: false },
          { id: 'ftf5', text: 'UPI is a digital payment system launched by NPCI.', answer: true },
          { id: 'ftf6', text: 'Mutual funds are always guaranteed to make profit.', answer: false },
          { id: 'ftf7', text: 'Insurance helps manage financial risk.', answer: true },
          { id: 'ftf8', text: 'A debit card uses your own money from your bank account.', answer: true },
        ]
      },
      { id: 7, name: 'Investment Types', description: 'Categorize investments by risk', type: 'categorize', stars: 2,
        categories: [
          { id: 'low', label: 'Low Risk', color: '#22c55e' },
          { id: 'medium', label: 'Medium Risk', color: '#f59e0b' },
          { id: 'high', label: 'High Risk', color: '#ef4444' },
        ],
        items: [
          { id: 'fit1', text: 'Fixed Deposit', category: 'low' }, { id: 'fit2', text: 'Mutual Funds', category: 'medium' },
          { id: 'fit3', text: 'Stocks', category: 'high' }, { id: 'fit4', text: 'PPF', category: 'low' },
          { id: 'fit5', text: 'Gold', category: 'medium' }, { id: 'fit6', text: 'Cryptocurrency', category: 'high' },
          { id: 'fit7', text: 'Savings Account', category: 'low' }, { id: 'fit8', text: 'Real Estate', category: 'medium' },
        ]
      },
      { id: 8, name: 'Tax Basics Match', description: 'Match tax terms to definitions', type: 'match', stars: 2,
        items: [
          { id: 'ftm1', left: 'Income Tax', right: 'Tax on earnings', match: 'ftm1' },
          { id: 'ftm2', left: 'GST', right: 'Tax on goods and services', match: 'ftm2' },
          { id: 'ftm3', left: 'TDS', right: 'Tax deducted at source', match: 'ftm3' },
          { id: 'ftm4', left: 'PAN Card', right: 'Tax identification number', match: 'ftm4' },
          { id: 'ftm5', left: 'ITR', right: 'Income Tax Return filing', match: 'ftm5' },
        ]
      },
      { id: 9, name: 'Budget Planning', description: 'Order steps to create a budget', type: 'order', stars: 2,
        items: [
          { id: 'fbp1', text: 'Calculate total monthly income', order: 1 }, { id: 'fbp2', text: 'List all fixed expenses', order: 2 },
          { id: 'fbp3', text: 'List variable expenses', order: 3 }, { id: 'fbp4', text: 'Set savings goal (at least 20%)', order: 4 },
          { id: 'fbp5', text: 'Allocate remaining for wants', order: 5 }, { id: 'fbp6', text: 'Review and adjust monthly', order: 6 },
        ]
      },
      { id: 10, name: 'Smart Money Quiz', description: 'Advanced financial literacy', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is the 50-30-20 budgeting rule?', options: ['50% needs, 30% wants, 20% savings', '50% savings, 30% needs, 20% wants', '50% wants, 30% savings, 20% needs', 'None'], answer: '50% needs, 30% wants, 20% savings' },
          { question: 'CIBIL score is related to?', options: ['Health', 'Credit worthiness', 'Education', 'Sports'], answer: 'Credit worthiness' },
          { question: 'What is an SIP?', options: ['Savings Insurance Plan', 'Systematic Investment Plan', 'Standard Interest Plan', 'Simple Income Plan'], answer: 'Systematic Investment Plan' },
          { question: 'EMI stands for?', options: ['Equal Monthly Income', 'Equated Monthly Installment', 'Extra Money Input', 'Emergency Money Insurance'], answer: 'Equated Monthly Installment' },
          { question: 'PPF maturity period is?', options: ['5 years', '10 years', '15 years', '20 years'], answer: '15 years' },
        ]
      },
    ]
  },

  career: {
    title: 'Career Explorer Lab',
    icon: Briefcase,
    color: '#4f46e5',
    info: 'Explore career paths, match your skills to professions, understand different streams, and plan your future career journey.',
    missions: [
      { id: 1, name: 'Career Streams', description: 'Categorize careers by stream', type: 'categorize', stars: 1,
        categories: [
          { id: 'science', label: 'Science', color: '#3b82f6' },
          { id: 'commerce', label: 'Commerce', color: '#22c55e' },
          { id: 'arts', label: 'Arts/Humanities', color: '#f59e0b' },
        ],
        items: [
          { id: 'ca1', text: 'Doctor', category: 'science' }, { id: 'ca2', text: 'CA (Chartered Accountant)', category: 'commerce' },
          { id: 'ca3', text: 'Journalist', category: 'arts' }, { id: 'ca4', text: 'Engineer', category: 'science' },
          { id: 'ca5', text: 'Investment Banker', category: 'commerce' }, { id: 'ca6', text: 'Lawyer', category: 'arts' },
          { id: 'ca7', text: 'Scientist', category: 'science' }, { id: 'ca8', text: 'Entrepreneur', category: 'commerce' },
          { id: 'ca9', text: 'Psychologist', category: 'arts' },
        ]
      },
      { id: 2, name: 'Skills Match', description: 'Match skills to suitable careers', type: 'match', stars: 2,
        items: [
          { id: 'sm1', left: 'Problem Solving', right: 'Software Engineer', match: 'sm1' },
          { id: 'sm2', left: 'Creativity', right: 'Graphic Designer', match: 'sm2' },
          { id: 'sm3', left: 'Communication', right: 'Marketing Manager', match: 'sm3' },
          { id: 'sm4', left: 'Analytical Thinking', right: 'Data Scientist', match: 'sm4' },
          { id: 'sm5', left: 'Leadership', right: 'Business Manager', match: 'sm5' },
        ]
      },
      { id: 3, name: 'Career Planning', description: 'Order the career planning steps', type: 'order', stars: 2,
        items: [
          { id: 'cp1', text: 'Self-assessment (interests & skills)', order: 1 },
          { id: 'cp2', text: 'Research career options', order: 2 },
          { id: 'cp3', text: 'Set short-term goals', order: 3 },
          { id: 'cp4', text: 'Choose education/training path', order: 4 },
          { id: 'cp5', text: 'Build skills and experience', order: 5 },
          { id: 'cp6', text: 'Apply and interview', order: 6 },
        ]
      },
      { id: 4, name: 'Career Quiz', description: 'Test your career knowledge', type: 'quiz', stars: 1,
        questions: [
          { question: 'To become a CA, you need to clear?', options: ['GATE', 'CAT', 'CA Foundation', 'CLAT'], answer: 'CA Foundation' },
          { question: 'MBBS stands for?', options: ['Medical Bachelor of Surgery', 'Bachelor of Medicine & Surgery', 'Medical Board of Studies', 'Bachelor of Medical Science'], answer: 'Bachelor of Medicine & Surgery' },
          { question: 'IIT is for?', options: ['Medical', 'Engineering', 'Law', 'Commerce'], answer: 'Engineering' },
        ]
      },
      { id: 5, name: 'Career Fill-In', description: 'Complete career pathway concepts', type: 'fill-blank', stars: 2,
        items: [
          { id: 'cafb1', sentence: 'To become an engineer, students typically take the ___ entrance exam.', answer: 'JEE' },
          { id: 'cafb2', sentence: 'A ___ is a document summarizing your education and work experience.', answer: 'resume' },
          { id: 'cafb3', sentence: 'CA stands for Chartered ___.', answer: 'Accountant' },
          { id: 'cafb4', sentence: 'An ___ is a temporary work experience to gain practical skills.', answer: 'internship' },
        ],
        distractors: ['NEET', 'portfolio', 'Analyst']
      },
      { id: 6, name: 'Career Fact Check', description: 'True or False about careers', type: 'true-false', stars: 3,
        statements: [
          { id: 'catf1', text: 'You need a medical degree to become a pharmacist.', answer: false },
          { id: 'catf2', text: 'IIMs are premier management institutes in India.', answer: true },
          { id: 'catf3', text: 'A B.Tech degree is required to work in IT.', answer: false },
          { id: 'catf4', text: 'CLAT is the entrance exam for law schools in India.', answer: true },
          { id: 'catf5', text: 'Entrepreneurship requires a specific degree.', answer: false },
          { id: 'catf6', text: 'Soft skills are as important as technical skills in most careers.', answer: true },
        ]
      },
      { id: 7, name: 'Resume Building', description: 'Order resume sections', type: 'order', stars: 2,
        items: [
          { id: 'crb1', text: 'Contact Information', order: 1 }, { id: 'crb2', text: 'Objective/Summary', order: 2 },
          { id: 'crb3', text: 'Education', order: 3 }, { id: 'crb4', text: 'Work Experience/Projects', order: 4 },
          { id: 'crb5', text: 'Skills', order: 5 }, { id: 'crb6', text: 'Achievements and Awards', order: 6 },
        ]
      },
      { id: 8, name: 'Interview Skills', description: 'Categorize interview behaviors', type: 'categorize', stars: 2,
        categories: [
          { id: 'do', label: 'Do (Good)', color: '#22c55e' },
          { id: 'dont', label: 'Don\'t (Bad)', color: '#ef4444' },
        ],
        items: [
          { id: 'cis1', text: 'Maintain eye contact', category: 'do' }, { id: 'cis2', text: 'Arrive late', category: 'dont' },
          { id: 'cis3', text: 'Research the company', category: 'do' }, { id: 'cis4', text: 'Use phone during interview', category: 'dont' },
          { id: 'cis5', text: 'Dress professionally', category: 'do' }, { id: 'cis6', text: 'Speak negatively about past employer', category: 'dont' },
          { id: 'cis7', text: 'Ask thoughtful questions', category: 'do' }, { id: 'cis8', text: 'Lie about qualifications', category: 'dont' },
        ]
      },
      { id: 9, name: 'Emerging Careers Match', description: 'Match new-age careers to fields', type: 'match', stars: 2,
        items: [
          { id: 'cem1', left: 'Data Scientist', right: 'Analyzes large datasets for insights', match: 'cem1' },
          { id: 'cem2', left: 'UX Designer', right: 'Designs user-friendly experiences', match: 'cem2' },
          { id: 'cem3', left: 'Cybersecurity Analyst', right: 'Protects systems from attacks', match: 'cem3' },
          { id: 'cem4', left: 'Content Creator', right: 'Creates digital media content', match: 'cem4' },
          { id: 'cem5', left: 'AI Engineer', right: 'Builds intelligent systems', match: 'cem5' },
        ]
      },
      { id: 10, name: 'Career Planning Quiz', description: 'Advanced career planning test', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is networking in career context?', options: ['Computer networks', 'Building professional relationships', 'Social media only', 'Job searching'], answer: 'Building professional relationships' },
          { question: 'A portfolio is useful for?', options: ['Only artists', 'Showcasing work to employers', 'Only students', 'Tax filing'], answer: 'Showcasing work to employers' },
          { question: 'What is freelancing?', options: ['Working for free', 'Self-employed project work', 'Government job', 'Unpaid internship'], answer: 'Self-employed project work' },
          { question: 'LinkedIn is primarily used for?', options: ['Gaming', 'Professional networking', 'Shopping', 'News'], answer: 'Professional networking' },
          { question: 'What does upskilling mean?', options: ['Quitting a job', 'Learning new skills for career growth', 'Working overtime', 'Changing careers'], answer: 'Learning new skills for career growth' },
        ]
      },
    ]
  },

  'competitive-exam': {
    title: 'Competitive Exam Prep',
    icon: Trophy,
    color: '#dc2626',
    info: 'Prepare for competitive exams like NTSE, Olympiads, JEE, NEET, and more with interactive practice and tips.',
    missions: [
      { id: 1, name: 'Exam Match', description: 'Match exams to their domains', type: 'match', stars: 1,
        items: [
          { id: 'ce1', left: 'JEE Main', right: 'Engineering entrance', match: 'ce1' },
          { id: 'ce2', left: 'NEET', right: 'Medical entrance', match: 'ce2' },
          { id: 'ce3', left: 'CLAT', right: 'Law entrance', match: 'ce3' },
          { id: 'ce4', left: 'NTSE', right: 'National talent search', match: 'ce4' },
          { id: 'ce5', left: 'CAT', right: 'Management entrance', match: 'ce5' },
        ]
      },
      { id: 2, name: 'National vs State', description: 'Categorize exams', type: 'categorize', stars: 1,
        categories: [
          { id: 'national', label: 'National Level', color: '#3b82f6' },
          { id: 'state', label: 'State Level', color: '#22c55e' },
        ],
        items: [
          { id: 'ns1', text: 'JEE Main', category: 'national' }, { id: 'ns2', text: 'MHT-CET', category: 'state' },
          { id: 'ns3', text: 'NEET', category: 'national' }, { id: 'ns4', text: 'KCET', category: 'state' },
          { id: 'ns5', text: 'NTSE', category: 'national' }, { id: 'ns6', text: 'AP EAMCET', category: 'state' },
        ]
      },
      { id: 3, name: 'Exam Prep Steps', description: 'Order the preparation strategy', type: 'order', stars: 2,
        items: [
          { id: 'ep1', text: 'Understand syllabus and pattern', order: 1 },
          { id: 'ep2', text: 'Create a study timetable', order: 2 },
          { id: 'ep3', text: 'Study concepts from NCERT', order: 3 },
          { id: 'ep4', text: 'Practice previous year papers', order: 4 },
          { id: 'ep5', text: 'Take mock tests', order: 5 },
          { id: 'ep6', text: 'Revise weak topics', order: 6 },
        ]
      },
      { id: 4, name: 'Exam Quiz', description: 'General knowledge about competitive exams', type: 'quiz', stars: 2,
        questions: [
          { question: 'NTSE exam is for which class?', options: ['8th', '10th', '12th', 'Graduate'], answer: '10th' },
          { question: 'JEE Advanced is for admission to?', options: ['NIT', 'IIT', 'AIIMS', 'IIM'], answer: 'IIT' },
          { question: 'NEET is conducted by?', options: ['CBSE', 'NTA', 'State Board', 'ICSE'], answer: 'NTA' },
          { question: 'Full form of UPSC?', options: ['Union Public Service Commission', 'United Public Service Council', 'Universal Public Service Commission', 'Union Public Selection Committee'], answer: 'Union Public Service Commission' },
        ]
      },
      { id: 5, name: 'Exam Concepts Fill-In', description: 'Complete exam preparation concepts', type: 'fill-blank', stars: 2,
        items: [
          { id: 'cefb1', sentence: 'JEE stands for Joint Entrance ___.', answer: 'Examination' },
          { id: 'cefb2', sentence: 'NEET is conducted by ___ (National Testing Agency).', answer: 'NTA' },
          { id: 'cefb3', sentence: 'The NTSE scholarship is given to students of class ___.', answer: '10th' },
          { id: 'cefb4', sentence: 'UPSC conducts the ___ Services Examination.', answer: 'Civil' },
        ],
        distractors: ['Exam', 'CBSE', '12th']
      },
      { id: 6, name: 'Exam Fact Check', description: 'True or False about exams', type: 'true-false', stars: 3,
        statements: [
          { id: 'cetf1', text: 'JEE Main score is used for admission to IITs.', answer: false },
          { id: 'cetf2', text: 'NEET is the only medical entrance exam in India.', answer: true },
          { id: 'cetf3', text: 'Olympiads test advanced knowledge beyond school syllabus.', answer: true },
          { id: 'cetf4', text: 'CAT exam is for engineering admissions.', answer: false },
          { id: 'cetf5', text: 'UPSC is one of the toughest exams in India.', answer: true },
          { id: 'cetf6', text: 'NTSE provides scholarships for school students.', answer: true },
        ]
      },
      { id: 7, name: 'Exam Pattern Match', description: 'Match exams to their patterns', type: 'match', stars: 2,
        items: [
          { id: 'cepm1', left: 'JEE Main', right: 'MCQ + Numerical, 3 hours', match: 'cepm1' },
          { id: 'cepm2', left: 'NEET', right: '180 MCQs, 3 hours 20 min', match: 'cepm2' },
          { id: 'cepm3', left: 'UPSC Prelims', right: 'GS + CSAT papers', match: 'cepm3' },
          { id: 'cepm4', left: 'CAT', right: 'VARC, DILR, QA sections', match: 'cepm4' },
          { id: 'cepm5', left: 'NTSE', right: 'MAT + SAT two-stage exam', match: 'cepm5' },
        ]
      },
      { id: 8, name: 'Study Techniques', description: 'Categorize study methods by effectiveness', type: 'categorize', stars: 2,
        categories: [
          { id: 'active', label: 'Active Learning', color: '#22c55e' },
          { id: 'passive', label: 'Passive Learning', color: '#f59e0b' },
        ],
        items: [
          { id: 'cest1', text: 'Practice problems', category: 'active' }, { id: 'cest2', text: 'Just reading notes', category: 'passive' },
          { id: 'cest3', text: 'Teaching others', category: 'active' }, { id: 'cest4', text: 'Highlighting text', category: 'passive' },
          { id: 'cest5', text: 'Mock tests', category: 'active' }, { id: 'cest6', text: 'Watching videos only', category: 'passive' },
          { id: 'cest7', text: 'Flashcard recall', category: 'active' }, { id: 'cest8', text: 'Re-reading textbook', category: 'passive' },
        ]
      },
      { id: 9, name: 'Exam Timeline', description: 'Order typical exam preparation timeline', type: 'order', stars: 2,
        items: [
          { id: 'cet1', text: 'Register for the exam', order: 1 }, { id: 'cet2', text: 'Complete syllabus once (foundation)', order: 2 },
          { id: 'cet3', text: 'Solve previous year papers', order: 3 }, { id: 'cet4', text: 'Take full-length mock tests', order: 4 },
          { id: 'cet5', text: 'Analyze mistakes and revise weak areas', order: 5 }, { id: 'cet6', text: 'Final revision and exam day prep', order: 6 },
        ]
      },
      { id: 10, name: 'Scholarship & Awards Quiz', description: 'Test knowledge on scholarships', type: 'quiz', stars: 3,
        questions: [
          { question: 'INSPIRE scholarship is given by?', options: ['UGC', 'DST (Dept of Science & Tech)', 'CBSE', 'NCERT'], answer: 'DST (Dept of Science & Tech)' },
          { question: 'Kishore Vaigyanik Protsahan Yojana (KVPY) is for?', options: ['Arts students', 'Science students', 'Commerce students', 'All students'], answer: 'Science students' },
          { question: 'National Merit Scholarship is based on?', options: ['Income only', 'Merit + income', 'Age only', 'State only'], answer: 'Merit + income' },
          { question: 'SOF stands for?', options: ['School of Finance', 'Science Olympiad Foundation', 'Student Office Forum', 'State Olympiad Fund'], answer: 'Science Olympiad Foundation' },
          { question: 'Which exam qualifies for KVPY fellowship?', options: ['JEE', 'KVPY aptitude test', 'NEET', 'CAT'], answer: 'KVPY aptitude test' },
        ]
      },
    ]
  },

  'advanced-math': {
    title: 'Advanced Math Lab',
    icon: BarChart3,
    color: '#4f46e5',
    info: 'Explore advanced mathematics — fractions, probability, percentages, and graph interpretation through interactive visual exercises.',
    missions: [
      { id: 1, name: 'Fraction Match', description: 'Match fractions to their decimal equivalents', type: 'match', stars: 1,
        items: [
          { id: 'fm1', left: '1/2', right: '0.5', match: 'fm1' },
          { id: 'fm2', left: '1/4', right: '0.25', match: 'fm2' },
          { id: 'fm3', left: '3/4', right: '0.75', match: 'fm3' },
          { id: 'fm4', left: '1/3', right: '0.333...', match: 'fm4' },
          { id: 'fm5', left: '1/5', right: '0.2', match: 'fm5' },
        ]
      },
      { id: 2, name: 'Number Types', description: 'Categorize numbers correctly', type: 'categorize', stars: 1,
        categories: [
          { id: 'rational', label: 'Rational Numbers', color: '#3b82f6' },
          { id: 'irrational', label: 'Irrational Numbers', color: '#ef4444' },
        ],
        items: [
          { id: 'nt1', text: '3/4', category: 'rational' }, { id: 'nt2', text: 'PI', category: 'irrational' },
          { id: 'nt3', text: '0.5', category: 'rational' }, { id: 'nt4', text: 'Root 2', category: 'irrational' },
          { id: 'nt5', text: '-7', category: 'rational' }, { id: 'nt6', text: 'Root 3', category: 'irrational' },
        ]
      },
      { id: 3, name: 'BODMAS Order', description: 'Order the operations priority', type: 'order', stars: 2,
        instruction: 'Arrange operations in order of priority (first to last):',
        items: [
          { id: 'bo1', text: 'Brackets', order: 1 }, { id: 'bo2', text: 'Orders (Powers/Roots)', order: 2 },
          { id: 'bo3', text: 'Division', order: 3 }, { id: 'bo4', text: 'Multiplication', order: 4 },
          { id: 'bo5', text: 'Addition', order: 5 }, { id: 'bo6', text: 'Subtraction', order: 6 },
        ]
      },
      { id: 4, name: 'Math Quiz', description: 'Test your math knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'What is 25% of 200?', options: ['25', '50', '75', '100'], answer: '50' },
          { question: 'Probability of heads in a coin toss?', options: ['1/4', '1/3', '1/2', '1'], answer: '1/2' },
          { question: 'Area of circle formula?', options: ['2PIr', 'PI r squared', '2PI r squared', 'PI d'], answer: 'PI r squared' },
          { question: 'What is the LCM of 4 and 6?', options: ['2', '12', '24', '6'], answer: '12' },
          { question: 'Square root of 144?', options: ['10', '11', '12', '14'], answer: '12' },
        ]
      },
      { id: 5, name: 'Math Concepts Fill-In', description: 'Complete the mathematical statements', type: 'fill-blank', stars: 3,
        instruction: 'Drag the correct term to fill each blank:',
        items: [
          { id: 'amfb1', sentence: 'The area of a circle is PI times the ___ squared.', answer: 'radius' },
          { id: 'amfb2', sentence: 'A triangle with all sides equal is called ___.', answer: 'equilateral' },
          { id: 'amfb3', sentence: 'The ___ of a number is how many times it is multiplied by itself.', answer: 'power' },
          { id: 'amfb4', sentence: 'The sum of angles in a triangle is ___ degrees.', answer: '180' },
          { id: 'amfb5', sentence: 'LCM stands for Least Common ___.', answer: 'Multiple' },
        ],
        distractors: ['diameter', 'isosceles', '360']
      },
      { id: 6, name: 'Math True or False', description: 'Rapid fire math facts', type: 'true-false', stars: 3,
        statements: [
          { id: 'amtf1', text: 'The square root of 2 is a rational number.', answer: false },
          { id: 'amtf2', text: 'Zero is an even number.', answer: true },
          { id: 'amtf3', text: 'The sum of interior angles of a quadrilateral is 360 degrees.', answer: true },
          { id: 'amtf4', text: 'PI is exactly equal to 22/7.', answer: false },
          { id: 'amtf5', text: 'A prime number has exactly two factors.', answer: true },
          { id: 'amtf6', text: 'The product of two negative numbers is negative.', answer: false },
          { id: 'amtf7', text: '1 is a prime number.', answer: false },
          { id: 'amtf8', text: 'The HCF of two prime numbers is always 1.', answer: true },
        ]
      },
      { id: 7, name: 'Geometry Formulas', description: 'Match shapes to area formulas', type: 'match', stars: 2,
        items: [
          { id: 'amgf1', left: 'Circle', right: 'PI x r squared', match: 'amgf1' },
          { id: 'amgf2', left: 'Triangle', right: '0.5 x base x height', match: 'amgf2' },
          { id: 'amgf3', left: 'Rectangle', right: 'length x width', match: 'amgf3' },
          { id: 'amgf4', left: 'Trapezium', right: '0.5 x (a+b) x height', match: 'amgf4' },
          { id: 'amgf5', left: 'Parallelogram', right: 'base x height', match: 'amgf5' },
        ]
      },
      { id: 8, name: 'Number Properties', description: 'Categorize numbers by property', type: 'categorize', stars: 2,
        categories: [
          { id: 'prime', label: 'Prime Numbers', color: '#3b82f6' },
          { id: 'composite', label: 'Composite Numbers', color: '#f59e0b' },
        ],
        items: [
          { id: 'amnp1', text: '2', category: 'prime' }, { id: 'amnp2', text: '4', category: 'composite' },
          { id: 'amnp3', text: '7', category: 'prime' }, { id: 'amnp4', text: '9', category: 'composite' },
          { id: 'amnp5', text: '11', category: 'prime' }, { id: 'amnp6', text: '15', category: 'composite' },
          { id: 'amnp7', text: '13', category: 'prime' }, { id: 'amnp8', text: '21', category: 'composite' },
        ]
      },
      { id: 9, name: 'Algebra Fill-In', description: 'Complete algebraic statements', type: 'fill-blank', stars: 3,
        items: [
          { id: 'amaf1', sentence: 'The square of (a+b) equals a squared + 2ab + b ___.', answer: 'squared' },
          { id: 'amaf2', sentence: 'If x + 5 = 12, then x = ___.', answer: '7' },
          { id: 'amaf3', sentence: 'The slope of a horizontal line is ___.', answer: '0' },
          { id: 'amaf4', sentence: 'A ___ equation has the highest power of 2.', answer: 'quadratic' },
          { id: 'amaf5', sentence: 'The product of a number and its reciprocal is ___.', answer: '1' },
        ],
        distractors: ['cubed', '5', 'linear']
      },
      { id: 10, name: 'Advanced Math Quiz', description: 'Challenge your math skills', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is the value of 0 factorial (0!)?', options: ['0', '1', 'Undefined', 'Infinity'], answer: '1' },
          { question: 'Sum of first 10 natural numbers?', options: ['45', '55', '50', '60'], answer: '55' },
          { question: 'What is the GCD of 12 and 18?', options: ['2', '3', '6', '9'], answer: '6' },
          { question: 'How many degrees in a right angle?', options: ['45', '90', '180', '360'], answer: '90' },
          { question: 'What is 2 to the power of 10?', options: ['512', '1000', '1024', '2048'], answer: '1024' },
        ]
      },
    ]
  },

  biology: {
    title: 'Biology Lab',
    icon: Dna,
    color: '#16a34a',
    info: 'Explore life sciences aligned with CBSE & ICSE — cell biology, human body systems, ecology, genetics, and evolution.',
    missions: [
      { id: 1, name: 'Cell Organelles', description: 'Match organelles to their functions', type: 'match', stars: 1,
        leftLabel: 'Organelle', rightLabel: 'Function',
        items: [
          { id: 'bo1', left: 'Nucleus', right: 'Controls cell activities', match: 'bo1' },
          { id: 'bo2', left: 'Mitochondria', right: 'Produces energy (ATP)', match: 'bo2' },
          { id: 'bo3', left: 'Ribosome', right: 'Synthesizes proteins', match: 'bo3' },
          { id: 'bo4', left: 'Golgi Apparatus', right: 'Packages & transports', match: 'bo4' },
          { id: 'bo5', left: 'Lysosome', right: 'Digests waste material', match: 'bo5' },
          { id: 'bo6', left: 'Endoplasmic Reticulum', right: 'Transport network', match: 'bo6' },
        ]
      },
      { id: 2, name: 'Body Systems', description: 'Categorize organs by body system', type: 'categorize', stars: 2,
        categories: [
          { id: 'circulatory', label: 'Circulatory System', color: '#ef4444' },
          { id: 'respiratory', label: 'Respiratory System', color: '#3b82f6' },
          { id: 'digestive', label: 'Digestive System', color: '#22c55e' },
        ],
        items: [
          { id: 'bs1', text: 'Heart', category: 'circulatory' }, { id: 'bs2', text: 'Lungs', category: 'respiratory' },
          { id: 'bs3', text: 'Stomach', category: 'digestive' }, { id: 'bs4', text: 'Arteries', category: 'circulatory' },
          { id: 'bs5', text: 'Trachea', category: 'respiratory' }, { id: 'bs6', text: 'Small Intestine', category: 'digestive' },
          { id: 'bs7', text: 'Blood Vessels', category: 'circulatory' }, { id: 'bs8', text: 'Diaphragm', category: 'respiratory' },
          { id: 'bs9', text: 'Liver', category: 'digestive' },
        ]
      },
      { id: 3, name: 'Digestion Process', description: 'Order the steps of human digestion', type: 'order', stars: 2,
        items: [
          { id: 'dp1', text: 'Food enters mouth, chewing begins', order: 1 },
          { id: 'dp2', text: 'Saliva breaks down starch (amylase)', order: 2 },
          { id: 'dp3', text: 'Food travels down oesophagus', order: 3 },
          { id: 'dp4', text: 'Stomach acid digests proteins', order: 4 },
          { id: 'dp5', text: 'Small intestine absorbs nutrients', order: 5 },
          { id: 'dp6', text: 'Large intestine absorbs water', order: 6 },
          { id: 'dp7', text: 'Waste excreted via rectum', order: 7 },
        ]
      },
      { id: 4, name: 'Biology Quiz (CBSE/ICSE)', description: 'Test your biology knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'Which organelle is called the "powerhouse of the cell"?', options: ['Nucleus', 'Mitochondria', 'Ribosome', 'Golgi body'], answer: 'Mitochondria' },
          { question: 'What is the basic unit of life?', options: ['Atom', 'Molecule', 'Cell', 'Tissue'], answer: 'Cell' },
          { question: 'Which blood cells fight infections?', options: ['RBC', 'WBC', 'Platelets', 'Plasma'], answer: 'WBC' },
          { question: 'DNA stands for?', options: ['Deoxyribonucleic Acid', 'Dinitro Amino Acid', 'Dinucleotide Acid', 'Deoxynitro Acid'], answer: 'Deoxyribonucleic Acid' },
          { question: 'Photosynthesis occurs in which organelle?', options: ['Mitochondria', 'Chloroplast', 'Nucleus', 'Ribosome'], answer: 'Chloroplast' },
        ]
      },
      { id: 5, name: 'Bio Concepts Fill-In', description: 'Complete biology statements', type: 'fill-blank', stars: 3,
        items: [
          { id: 'bfb1', sentence: 'The ___ is the structural and functional unit of life.', answer: 'cell' },
          { id: 'bfb2', sentence: 'Red blood cells carry ___ to all parts of the body.', answer: 'oxygen' },
          { id: 'bfb3', sentence: 'The process by which plants make food using sunlight is called ___.', answer: 'photosynthesis' },
          { id: 'bfb4', sentence: '___ is the hereditary material found in the nucleus.', answer: 'DNA' },
          { id: 'bfb5', sentence: 'The ___ system is responsible for fighting diseases.', answer: 'immune' },
        ],
        distractors: ['RNA', 'carbon dioxide', 'nervous']
      },
      { id: 6, name: 'Biology Fact Check', description: 'Rapid fire biology facts', type: 'true-false', stars: 3,
        statements: [
          { id: 'btf1', text: 'Plants cells have a cell wall but animal cells do not.', answer: true },
          { id: 'btf2', text: 'Arteries carry deoxygenated blood.', answer: false },
          { id: 'btf3', text: 'The human body has 206 bones.', answer: true },
          { id: 'btf4', text: 'Fungi are autotrophs that make their own food.', answer: false },
          { id: 'btf5', text: 'Chromosomes are made of DNA and protein.', answer: true },
          { id: 'btf6', text: 'The smallest bone in the human body is in the ear (stapes).', answer: true },
          { id: 'btf7', text: 'Osmosis is the movement of solute from low to high concentration.', answer: false },
          { id: 'btf8', text: 'Hemoglobin gives blood its red colour.', answer: true },
        ]
      },
      { id: 7, name: 'Cell Organelles Match', description: 'Match organelles to their functions', type: 'match', stars: 2,
        items: [
          { id: 'bco1', left: 'Mitochondria', right: 'Powerhouse of the cell', match: 'bco1' },
          { id: 'bco2', left: 'Ribosome', right: 'Protein synthesis', match: 'bco2' },
          { id: 'bco3', left: 'Nucleus', right: 'Contains genetic material', match: 'bco3' },
          { id: 'bco4', left: 'Cell Membrane', right: 'Controls what enters/exits cell', match: 'bco4' },
          { id: 'bco5', left: 'Chloroplast', right: 'Photosynthesis (in plants)', match: 'bco5' },
        ]
      },
      { id: 8, name: 'Animal vs Plant Cell', description: 'Categorize cell features', type: 'categorize', stars: 2,
        categories: [
          { id: 'both', label: 'Both Cells', color: '#3b82f6' },
          { id: 'plant', label: 'Plant Cell Only', color: '#22c55e' },
          { id: 'animal', label: 'Animal Cell Only', color: '#f59e0b' },
        ],
        items: [
          { id: 'bap1', text: 'Cell wall', category: 'plant' }, { id: 'bap2', text: 'Nucleus', category: 'both' },
          { id: 'bap3', text: 'Centrioles', category: 'animal' }, { id: 'bap4', text: 'Chloroplast', category: 'plant' },
          { id: 'bap5', text: 'Mitochondria', category: 'both' }, { id: 'bap6', text: 'Lysosomes', category: 'animal' },
          { id: 'bap7', text: 'Large vacuole', category: 'plant' }, { id: 'bap8', text: 'Cell membrane', category: 'both' },
        ]
      },
      { id: 9, name: 'Digestive System Order', description: 'Order organs in the digestive tract', type: 'order', stars: 2,
        items: [
          { id: 'bdo1', text: 'Mouth', order: 1 }, { id: 'bdo2', text: 'Esophagus', order: 2 },
          { id: 'bdo3', text: 'Stomach', order: 3 }, { id: 'bdo4', text: 'Small Intestine', order: 4 },
          { id: 'bdo5', text: 'Large Intestine', order: 5 }, { id: 'bdo6', text: 'Rectum', order: 6 },
        ]
      },
      { id: 10, name: 'Genetics Quiz', description: 'Test your genetics knowledge', type: 'quiz', stars: 3,
        questions: [
          { question: 'Who is the father of genetics?', options: ['Darwin', 'Mendel', 'Watson', 'Crick'], answer: 'Mendel' },
          { question: 'What is phenotype?', options: ['Genetic code', 'Observable traits', 'DNA sequence', 'Chromosome number'], answer: 'Observable traits' },
          { question: 'Dominant genes are represented by?', options: ['Lowercase', 'Uppercase', 'Numbers', 'Symbols'], answer: 'Uppercase' },
          { question: 'How many base pairs does human DNA have?', options: ['3 million', '3 billion', '300 million', '30 billion'], answer: '3 billion' },
          { question: 'XX chromosomes indicate?', options: ['Male', 'Female', 'Either', 'Neither'], answer: 'Female' },
        ]
      },
    ]
  },

  botany: {
    title: 'Botany Lab',
    icon: Leaf,
    color: '#15803d',
    info: 'Study plant science aligned with CBSE & ICSE — plant anatomy, photosynthesis, classification, reproduction, and ecology.',
    missions: [
      { id: 1, name: 'Plant Parts', description: 'Match plant parts to their functions', type: 'match', stars: 1,
        items: [
          { id: 'bp1', left: 'Root', right: 'Absorbs water & minerals', match: 'bp1' },
          { id: 'bp2', left: 'Stem', right: 'Transports water & nutrients', match: 'bp2' },
          { id: 'bp3', left: 'Leaf', right: 'Photosynthesis site', match: 'bp3' },
          { id: 'bp4', left: 'Flower', right: 'Reproductive organ', match: 'bp4' },
          { id: 'bp5', left: 'Fruit', right: 'Protects & disperses seeds', match: 'bp5' },
          { id: 'bp6', left: 'Xylem', right: 'Conducts water upward', match: 'bp6' },
        ]
      },
      { id: 2, name: 'Plant Classification', description: 'Sort plants by type', type: 'categorize', stars: 2,
        categories: [
          { id: 'angiosperm', label: 'Angiosperms (Flowering)', color: '#f59e0b' },
          { id: 'gymnosperm', label: 'Gymnosperms (Non-Flowering)', color: '#22c55e' },
          { id: 'pteridophyte', label: 'Pteridophytes (Ferns)', color: '#8b5cf6' },
        ],
        items: [
          { id: 'pc1', text: 'Rose', category: 'angiosperm' }, { id: 'pc2', text: 'Pine', category: 'gymnosperm' },
          { id: 'pc3', text: 'Fern', category: 'pteridophyte' }, { id: 'pc4', text: 'Mango', category: 'angiosperm' },
          { id: 'pc5', text: 'Cycas', category: 'gymnosperm' }, { id: 'pc6', text: 'Horsetail', category: 'pteridophyte' },
          { id: 'pc7', text: 'Sunflower', category: 'angiosperm' }, { id: 'pc8', text: 'Cedar', category: 'gymnosperm' },
        ]
      },
      { id: 3, name: 'Photosynthesis Steps', description: 'Order the photosynthesis process', type: 'order', stars: 2,
        items: [
          { id: 'ps1', text: 'Sunlight hits the chloroplast', order: 1 },
          { id: 'ps2', text: 'Chlorophyll absorbs light energy', order: 2 },
          { id: 'ps3', text: 'Water molecules are split (photolysis)', order: 3 },
          { id: 'ps4', text: 'Oxygen is released as by-product', order: 4 },
          { id: 'ps5', text: 'CO2 is fixed into glucose (Calvin cycle)', order: 5 },
          { id: 'ps6', text: 'Glucose is stored as starch', order: 6 },
        ]
      },
      { id: 4, name: 'Botany Quiz (CBSE/ICSE)', description: 'Test your plant science knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'Which tissue transports food in plants?', options: ['Xylem', 'Phloem', 'Cambium', 'Cork'], answer: 'Phloem' },
          { question: 'The green pigment in leaves is?', options: ['Carotene', 'Xanthophyll', 'Chlorophyll', 'Anthocyanin'], answer: 'Chlorophyll' },
          { question: 'Pollination by wind is called?', options: ['Entomophily', 'Anemophily', 'Hydrophily', 'Zoophily'], answer: 'Anemophily' },
          { question: 'Which gas is used during photosynthesis?', options: ['Oxygen', 'Nitrogen', 'Carbon Dioxide', 'Hydrogen'], answer: 'Carbon Dioxide' },
          { question: 'Stomata help in?', options: ['Absorption', 'Gas exchange', 'Reproduction', 'Growth'], answer: 'Gas exchange' },
        ]
      },
      { id: 5, name: 'Plant Science Fill-In', description: 'Complete botany concepts', type: 'fill-blank', stars: 3,
        items: [
          { id: 'btfb1', sentence: '___ is the green pigment that captures sunlight for photosynthesis.', answer: 'Chlorophyll' },
          { id: 'btfb2', sentence: 'The ___ transports water from roots to leaves.', answer: 'xylem' },
          { id: 'btfb3', sentence: 'Plants release ___ as a by-product of photosynthesis.', answer: 'oxygen' },
          { id: 'btfb4', sentence: 'Tiny pores on leaves called ___ allow gas exchange.', answer: 'stomata' },
          { id: 'btfb5', sentence: 'The transfer of pollen to the stigma is called ___.', answer: 'pollination' },
        ],
        distractors: ['phloem', 'carbon dioxide', 'petals']
      },
      { id: 6, name: 'Botany Fact Check', description: 'True or False plant facts', type: 'true-false', stars: 3,
        statements: [
          { id: 'bttf1', text: 'Mushrooms are classified as plants.', answer: false },
          { id: 'bttf2', text: 'Xylem carries water and phloem carries food.', answer: true },
          { id: 'bttf3', text: 'Photosynthesis occurs in the mitochondria.', answer: false },
          { id: 'bttf4', text: 'Gymnosperms produce seeds but not flowers.', answer: true },
          { id: 'bttf5', text: 'Transpiration is the loss of water from plant leaves.', answer: true },
          { id: 'bttf6', text: 'All roots grow underground.', answer: false },
          { id: 'bttf7', text: 'The pistil is the male reproductive part of a flower.', answer: false },
          { id: 'bttf8', text: 'Leguminous plants fix nitrogen in the soil.', answer: true },
        ]
      },
      { id: 7, name: 'Flower Parts Match', description: 'Match flower parts to functions', type: 'match', stars: 2,
        items: [
          { id: 'bfp1', left: 'Petal', right: 'Attracts pollinators', match: 'bfp1' },
          { id: 'bfp2', left: 'Stamen', right: 'Male reproductive part', match: 'bfp2' },
          { id: 'bfp3', left: 'Pistil', right: 'Female reproductive part', match: 'bfp3' },
          { id: 'bfp4', left: 'Sepal', right: 'Protects the flower bud', match: 'bfp4' },
          { id: 'bfp5', left: 'Ovary', right: 'Contains ovules, becomes fruit', match: 'bfp5' },
        ]
      },
      { id: 8, name: 'Seed Dispersal', description: 'Categorize seeds by dispersal method', type: 'categorize', stars: 2,
        categories: [
          { id: 'wind', label: 'Wind Dispersal', color: '#93c5fd' },
          { id: 'water', label: 'Water Dispersal', color: '#67e8f9' },
          { id: 'animal', label: 'Animal Dispersal', color: '#fca5a5' },
        ],
        items: [
          { id: 'bsd1', text: 'Dandelion', category: 'wind' }, { id: 'bsd2', text: 'Coconut', category: 'water' },
          { id: 'bsd3', text: 'Burdock (hooks)', category: 'animal' }, { id: 'bsd4', text: 'Maple (samara)', category: 'wind' },
          { id: 'bsd5', text: 'Lotus', category: 'water' }, { id: 'bsd6', text: 'Berries (eaten by birds)', category: 'animal' },
        ]
      },
      { id: 9, name: 'Plant Growth Order', description: 'Order stages of plant growth', type: 'order', stars: 1,
        items: [
          { id: 'bpg1', text: 'Seed absorbs water (imbibition)', order: 1 }, { id: 'bpg2', text: 'Seed coat breaks (germination)', order: 2 },
          { id: 'bpg3', text: 'Root emerges (radicle)', order: 3 }, { id: 'bpg4', text: 'Shoot grows upward (plumule)', order: 4 },
          { id: 'bpg5', text: 'Leaves develop, photosynthesis begins', order: 5 }, { id: 'bpg6', text: 'Plant matures and flowers', order: 6 },
        ]
      },
      { id: 10, name: 'Advanced Botany Quiz', description: 'Challenge your plant knowledge', type: 'quiz', stars: 3,
        questions: [
          { question: 'Auxin is a plant hormone that controls?', options: ['Flowering', 'Growth and bending', 'Color', 'Taste'], answer: 'Growth and bending' },
          { question: 'Which plant part absorbs water?', options: ['Stem', 'Leaf', 'Root hair', 'Flower'], answer: 'Root hair' },
          { question: 'Respiration in plants occurs in?', options: ['Only leaves', 'Only roots', 'All living cells', 'Only flowers'], answer: 'All living cells' },
          { question: 'What is the function of cambium?', options: ['Photosynthesis', 'Secondary growth', 'Reproduction', 'Storage'], answer: 'Secondary growth' },
          { question: 'Venus flytrap is an example of?', options: ['Parasitic plant', 'Insectivorous plant', 'Saprophyte', 'Epiphyte'], answer: 'Insectivorous plant' },
        ]
      },
    ]
  },

  robotics: {
    title: 'Robotics Lab',
    icon: Bot,
    color: '#6366f1',
    info: 'Learn robotics fundamentals — robot components, sensors, actuators, programming logic, automation, and real-world applications.',
    missions: [
      { id: 1, name: 'Robot Components', description: 'Match robot parts to their roles', type: 'match', stars: 1,
        items: [
          { id: 'rc1', left: 'Microcontroller', right: 'Brain of the robot', match: 'rc1' },
          { id: 'rc2', left: 'Servo Motor', right: 'Precise angle movement', match: 'rc2' },
          { id: 'rc3', left: 'Ultrasonic Sensor', right: 'Measures distance', match: 'rc3' },
          { id: 'rc4', left: 'Battery', right: 'Power supply', match: 'rc4' },
          { id: 'rc5', left: 'Chassis', right: 'Robot body frame', match: 'rc5' },
          { id: 'rc6', left: 'Gripper', right: 'Picks up objects', match: 'rc6' },
        ]
      },
      { id: 2, name: 'Sensors vs Actuators', description: 'Categorize robot components', type: 'categorize', stars: 1,
        categories: [
          { id: 'sensor', label: 'Sensors (Input)', color: '#3b82f6' },
          { id: 'actuator', label: 'Actuators (Output)', color: '#f59e0b' },
        ],
        items: [
          { id: 'sa1', text: 'IR Sensor', category: 'sensor' }, { id: 'sa2', text: 'DC Motor', category: 'actuator' },
          { id: 'sa3', text: 'Camera', category: 'sensor' }, { id: 'sa4', text: 'LED', category: 'actuator' },
          { id: 'sa5', text: 'Temperature Sensor', category: 'sensor' }, { id: 'sa6', text: 'Buzzer', category: 'actuator' },
          { id: 'sa7', text: 'Gyroscope', category: 'sensor' }, { id: 'sa8', text: 'Servo Motor', category: 'actuator' },
        ]
      },
      { id: 3, name: 'Build a Robot', description: 'Order the robot building steps', type: 'order', stars: 2,
        items: [
          { id: 'rb1', text: 'Define the robot purpose/task', order: 1 },
          { id: 'rb2', text: 'Design the robot (blueprint)', order: 2 },
          { id: 'rb3', text: 'Assemble the chassis and motors', order: 3 },
          { id: 'rb4', text: 'Connect sensors and microcontroller', order: 4 },
          { id: 'rb5', text: 'Write and upload the code', order: 5 },
          { id: 'rb6', text: 'Test and debug the robot', order: 6 },
        ]
      },
      { id: 4, name: 'Robotics Quiz', description: 'Test your robotics knowledge', type: 'quiz', stars: 2,
        questions: [
          { question: 'Arduino is a type of?', options: ['Sensor', 'Motor', 'Microcontroller', 'Battery'], answer: 'Microcontroller' },
          { question: 'What does IoT stand for?', options: ['Internet of Things', 'Input of Technology', 'Integrated Online Tool', 'Interface of Terminals'], answer: 'Internet of Things' },
          { question: 'Which sensor detects obstacles?', options: ['Temperature', 'Ultrasonic', 'Humidity', 'Light'], answer: 'Ultrasonic' },
          { question: 'A line-follower robot uses which sensor?', options: ['Ultrasonic', 'IR (Infrared)', 'Pressure', 'GPS'], answer: 'IR (Infrared)' },
          { question: 'What language is commonly used to program Arduino?', options: ['Python', 'C/C++', 'Java', 'HTML'], answer: 'C/C++' },
        ]
      },
      { id: 5, name: 'Robotics Fill-In', description: 'Complete robotics concepts', type: 'fill-blank', stars: 2,
        items: [
          { id: 'rofb1', sentence: 'A ___ is the brain of a robot that processes instructions.', answer: 'microcontroller' },
          { id: 'rofb2', sentence: 'IoT stands for Internet of ___.', answer: 'Things' },
          { id: 'rofb3', sentence: 'A ___ motor allows precise angular rotation.', answer: 'servo' },
          { id: 'rofb4', sentence: 'Robots that work alongside humans are called ___ robots.', answer: 'collaborative' },
          { id: 'rofb5', sentence: 'The Arduino programming language is based on ___.', answer: 'C/C++' },
        ],
        distractors: ['processor', 'stepper', 'autonomous']
      },
      { id: 6, name: 'Robotics Fact Check', description: 'True or False about robots', type: 'true-false', stars: 3,
        statements: [
          { id: 'rotf1', text: 'All robots are humanoid in shape.', answer: false },
          { id: 'rotf2', text: 'Arduino is an open-source electronics platform.', answer: true },
          { id: 'rotf3', text: 'Sensors detect changes in the environment.', answer: true },
          { id: 'rotf4', text: 'Robots cannot be used in surgery.', answer: false },
          { id: 'rotf5', text: 'A drone is a type of unmanned aerial robot.', answer: true },
          { id: 'rotf6', text: 'Raspberry Pi can only be used for gaming.', answer: false },
          { id: 'rotf7', text: 'Industrial robots are used in car manufacturing.', answer: true },
          { id: 'rotf8', text: 'AI is essential for all robots to function.', answer: false },
        ]
      },
      { id: 7, name: 'Robot Types', description: 'Categorize robots by application', type: 'categorize', stars: 2,
        categories: [
          { id: 'industrial', label: 'Industrial Robots', color: '#6366f1' },
          { id: 'service', label: 'Service Robots', color: '#22c55e' },
          { id: 'exploration', label: 'Exploration Robots', color: '#f59e0b' },
        ],
        items: [
          { id: 'rt1', text: 'Welding robot arm', category: 'industrial' }, { id: 'rt2', text: 'Roomba vacuum', category: 'service' },
          { id: 'rt3', text: 'Mars Rover', category: 'exploration' }, { id: 'rt4', text: 'Assembly line robot', category: 'industrial' },
          { id: 'rt5', text: 'Surgical robot (Da Vinci)', category: 'service' }, { id: 'rt6', text: 'Underwater drone', category: 'exploration' },
          { id: 'rt7', text: 'Packaging robot', category: 'industrial' }, { id: 'rt8', text: 'Delivery robot', category: 'service' },
        ]
      },
      { id: 8, name: 'Circuit Building Order', description: 'Order steps to build a basic circuit', type: 'order', stars: 2,
        items: [
          { id: 'rcb1', text: 'Gather components (LED, resistor, wires, battery)', order: 1 },
          { id: 'rcb2', text: 'Place components on breadboard', order: 2 },
          { id: 'rcb3', text: 'Connect positive terminal to resistor', order: 3 },
          { id: 'rcb4', text: 'Connect resistor to LED anode', order: 4 },
          { id: 'rcb5', text: 'Connect LED cathode to negative terminal', order: 5 },
          { id: 'rcb6', text: 'Power on and test the circuit', order: 6 },
        ]
      },
      { id: 9, name: 'Arduino Pins Match', description: 'Match Arduino pin types to usage', type: 'match', stars: 3,
        items: [
          { id: 'rap1', left: 'Digital Pins', right: 'HIGH or LOW signals', match: 'rap1' },
          { id: 'rap2', left: 'Analog Pins', right: 'Read variable voltage (0-1023)', match: 'rap2' },
          { id: 'rap3', left: 'PWM Pins (~)', right: 'Simulate analog output', match: 'rap3' },
          { id: 'rap4', left: 'GND Pin', right: 'Ground connection', match: 'rap4' },
          { id: 'rap5', left: '5V Pin', right: 'Power supply output', match: 'rap5' },
        ]
      },
      { id: 10, name: 'Advanced Robotics Quiz', description: 'Challenge your robotics knowledge', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is ROS in robotics?', options: ['Robot Operating System', 'Robotic Output Signal', 'Remote Operation Software', 'Real-time Object Sensor'], answer: 'Robot Operating System' },
          { question: 'LIDAR is used for?', options: ['Heating', 'Distance mapping with laser', 'Communication', 'Charging'], answer: 'Distance mapping with laser' },
          { question: 'What does PWM stand for?', options: ['Pulse Width Modulation', 'Power With Motor', 'Programmed Wave Motion', 'Pin Wire Module'], answer: 'Pulse Width Modulation' },
          { question: 'Which robot explored Mars?', options: ['ASIMO', 'Curiosity', 'Sophia', 'Roomba'], answer: 'Curiosity' },
          { question: 'What is a cobot?', options: ['Competitive robot', 'Collaborative robot', 'Communication bot', 'Control bot'], answer: 'Collaborative robot' },
        ]
      },
    ]
  },

  'political-science': {
    title: 'Political Science Lab',
    icon: Landmark,
    color: '#b91c1c',
    info: 'Study governance and politics aligned with CBSE & ICSE — Indian Constitution, democracy, government structure, political ideologies, and international relations.',
    missions: [
      { id: 1, name: 'Government Branches', description: 'Match branches to their roles', type: 'match', stars: 1,
        items: [
          { id: 'ps1', left: 'Legislature', right: 'Makes laws (Parliament)', match: 'ps1' },
          { id: 'ps2', left: 'Executive', right: 'Implements laws (PM & Cabinet)', match: 'ps2' },
          { id: 'ps3', left: 'Judiciary', right: 'Interprets laws (Courts)', match: 'ps3' },
          { id: 'ps4', left: 'Lok Sabha', right: 'Lower House (People)', match: 'ps4' },
          { id: 'ps5', left: 'Rajya Sabha', right: 'Upper House (States)', match: 'ps5' },
          { id: 'ps6', left: 'Election Commission', right: 'Conducts free elections', match: 'ps6' },
        ]
      },
      { id: 2, name: 'Rights vs Duties', description: 'Categorize Fundamental Rights and Duties', type: 'categorize', stars: 2,
        categories: [
          { id: 'rights', label: 'Fundamental Rights', color: '#3b82f6' },
          { id: 'duties', label: 'Fundamental Duties', color: '#f59e0b' },
          { id: 'directive', label: 'Directive Principles', color: '#22c55e' },
        ],
        items: [
          { id: 'rd1', text: 'Right to Equality', category: 'rights' },
          { id: 'rd2', text: 'Protect national flag', category: 'duties' },
          { id: 'rd3', text: 'Equal pay for equal work', category: 'directive' },
          { id: 'rd4', text: 'Right to Freedom of Speech', category: 'rights' },
          { id: 'rd5', text: 'Promote harmony', category: 'duties' },
          { id: 'rd6', text: 'Free legal aid', category: 'directive' },
          { id: 'rd7', text: 'Right against Exploitation', category: 'rights' },
          { id: 'rd8', text: 'Protect environment', category: 'duties' },
          { id: 'rd9', text: 'Uniform Civil Code', category: 'directive' },
        ]
      },
      { id: 3, name: 'How a Bill Becomes Law', description: 'Order the law-making process', type: 'order', stars: 2,
        items: [
          { id: 'bl1', text: 'Bill introduced in Parliament', order: 1 },
          { id: 'bl2', text: 'First Reading (introduction)', order: 2 },
          { id: 'bl3', text: 'Second Reading (committee review)', order: 3 },
          { id: 'bl4', text: 'Third Reading (voting & debate)', order: 4 },
          { id: 'bl5', text: 'Passed to other House for approval', order: 5 },
          { id: 'bl6', text: "President's assent (becomes law)", order: 6 },
        ]
      },
      { id: 4, name: 'Political Science Quiz', description: 'Test your knowledge (CBSE/ICSE)', type: 'quiz', stars: 2,
        questions: [
          { question: 'Who is the head of state in India?', options: ['Prime Minister', 'President', 'Governor', 'Speaker'], answer: 'President' },
          { question: 'How many Fundamental Rights are there?', options: ['5', '6', '7', '8'], answer: '6' },
          { question: 'The Indian Constitution was adopted on?', options: ['15 Aug 1947', '26 Jan 1950', '26 Nov 1949', '2 Oct 1950'], answer: '26 Nov 1949' },
          { question: 'Who is called the Father of the Indian Constitution?', options: ['Mahatma Gandhi', 'Jawaharlal Nehru', 'B.R. Ambedkar', 'Sardar Patel'], answer: 'B.R. Ambedkar' },
          { question: 'Lok Sabha members are elected for how many years?', options: ['4', '5', '6', '7'], answer: '5' },
        ]
      },
      { id: 5, name: 'Constitution Fill-In', description: 'Complete constitutional concepts', type: 'fill-blank', stars: 3,
        items: [
          { id: 'psfb1', sentence: 'The Preamble declares India to be a Sovereign, Socialist, Secular, Democratic ___.', answer: 'Republic' },
          { id: 'psfb2', sentence: 'Dr. B.R. ___ is known as the Father of the Indian Constitution.', answer: 'Ambedkar' },
          { id: 'psfb3', sentence: 'The ___ is the guardian of the Constitution in India.', answer: 'Supreme Court' },
          { id: 'psfb4', sentence: 'India follows a ___ system of government with a PM as head.', answer: 'parliamentary' },
          { id: 'psfb5', sentence: 'The right to ___ is a fundamental right under Article 21.', answer: 'life' },
        ],
        distractors: ['Monarchy', 'Nehru', 'presidential']
      },
      { id: 6, name: 'Politics Fact Check', description: 'Rapid fire political facts', type: 'true-false', stars: 3,
        statements: [
          { id: 'pstf1', text: 'India has a presidential form of government.', answer: false },
          { id: 'pstf2', text: 'The Constitution of India is the longest written constitution.', answer: true },
          { id: 'pstf3', text: 'The President of India is elected directly by the people.', answer: false },
          { id: 'pstf4', text: 'Fundamental Rights are justiciable (enforceable by courts).', answer: true },
          { id: 'pstf5', text: 'Rajya Sabha members are directly elected by citizens.', answer: false },
          { id: 'pstf6', text: 'The Preamble is part of the Indian Constitution.', answer: true },
          { id: 'pstf7', text: 'The Supreme Court can declare a law unconstitutional.', answer: true },
          { id: 'pstf8', text: 'There are 29 states in India currently.', answer: false },
        ]
      },
      { id: 7, name: 'Indian Leaders Match', description: 'Match leaders to their contributions', type: 'match', stars: 2,
        items: [
          { id: 'psil1', left: 'Mahatma Gandhi', right: 'Father of the Nation', match: 'psil1' },
          { id: 'psil2', left: 'Jawaharlal Nehru', right: 'First Prime Minister', match: 'psil2' },
          { id: 'psil3', left: 'Sardar Patel', right: 'Iron Man of India', match: 'psil3' },
          { id: 'psil4', left: 'Subhash Chandra Bose', right: 'Founded Indian National Army', match: 'psil4' },
          { id: 'psil5', left: 'Dr. Rajendra Prasad', right: 'First President of India', match: 'psil5' },
        ]
      },
      { id: 8, name: 'Election Process', description: 'Order the Indian election process', type: 'order', stars: 2,
        items: [
          { id: 'pse1', text: 'Election Commission announces dates', order: 1 },
          { id: 'pse2', text: 'Candidates file nominations', order: 2 },
          { id: 'pse3', text: 'Campaigning period', order: 3 },
          { id: 'pse4', text: 'Polling day (voting with EVMs)', order: 4 },
          { id: 'pse5', text: 'Vote counting', order: 5 },
          { id: 'pse6', text: 'Results declared, government formed', order: 6 },
        ]
      },
      { id: 9, name: 'State vs Union', description: 'Categorize subjects by government list', type: 'categorize', stars: 3,
        categories: [
          { id: 'union', label: 'Union List (Central)', color: '#3b82f6' },
          { id: 'state', label: 'State List', color: '#22c55e' },
          { id: 'concurrent', label: 'Concurrent List', color: '#f59e0b' },
        ],
        items: [
          { id: 'psu1', text: 'Defence', category: 'union' }, { id: 'psu2', text: 'Police', category: 'state' },
          { id: 'psu3', text: 'Education', category: 'concurrent' }, { id: 'psu4', text: 'Foreign Affairs', category: 'union' },
          { id: 'psu5', text: 'Agriculture', category: 'state' }, { id: 'psu6', text: 'Forests', category: 'concurrent' },
          { id: 'psu7', text: 'Banking', category: 'union' }, { id: 'psu8', text: 'Public Health', category: 'state' },
        ]
      },
      { id: 10, name: 'Advanced Polity Quiz', description: 'Deep dive into Indian polity', type: 'quiz', stars: 3,
        questions: [
          { question: 'Which article deals with Right to Education?', options: ['Article 14', 'Article 19', 'Article 21A', 'Article 32'], answer: 'Article 21A' },
          { question: 'How many schedules does the Indian Constitution have?', options: ['8', '10', '12', '14'], answer: '12' },
          { question: 'Who appoints the Chief Justice of India?', options: ['PM', 'President', 'Parliament', 'Law Minister'], answer: 'President' },
          { question: 'RTI Act was passed in which year?', options: ['2000', '2003', '2005', '2010'], answer: '2005' },
          { question: 'Which part of the Constitution deals with Panchayati Raj?', options: ['Part VIII', 'Part IX', 'Part X', 'Part XI'], answer: 'Part IX' },
        ]
      },
    ]
  },

  // ==================== MACHINE LEARNING LABS ====================

  'ml-basics': {
    title: 'ML Basics Lab',
    icon: Brain,
    color: '#8b5cf6',
    info: 'Machine Learning is a branch of AI where computers learn from data to make predictions or decisions without being explicitly programmed.',
    missions: [
      { id: 1, name: 'Types of ML', description: 'Match ML types to their definitions', type: 'match', stars: 1,
        items: [
          { id: 'mlb1', left: 'Supervised Learning', right: 'Learns from labeled data', match: 'mlb1' },
          { id: 'mlb2', left: 'Unsupervised Learning', right: 'Finds patterns in unlabeled data', match: 'mlb2' },
          { id: 'mlb3', left: 'Reinforcement Learning', right: 'Learns by trial and reward', match: 'mlb3' },
          { id: 'mlb4', left: 'Deep Learning', right: 'Uses neural networks with many layers', match: 'mlb4' },
          { id: 'mlb5', left: 'Transfer Learning', right: 'Reuses a pre-trained model', match: 'mlb5' },
        ]
      },
      { id: 2, name: 'ML Applications', description: 'Categorize real-world ML applications', type: 'categorize', stars: 1,
        categories: [
          { id: 'vision', label: 'Computer Vision', color: '#3b82f6' },
          { id: 'nlp', label: 'NLP (Language)', color: '#22c55e' },
          { id: 'recomm', label: 'Recommendation', color: '#f59e0b' },
        ],
        items: [
          { id: 'mla1', text: 'Face Recognition', category: 'vision' },
          { id: 'mla2', text: 'Chatbots', category: 'nlp' },
          { id: 'mla3', text: 'Netflix Suggestions', category: 'recomm' },
          { id: 'mla4', text: 'Self-Driving Cars', category: 'vision' },
          { id: 'mla5', text: 'Spam Detection', category: 'nlp' },
          { id: 'mla6', text: 'Music Playlists', category: 'recomm' },
          { id: 'mla7', text: 'Language Translation', category: 'nlp' },
          { id: 'mla8', text: 'Object Detection', category: 'vision' },
          { id: 'mla9', text: 'Amazon Products', category: 'recomm' },
        ]
      },
      { id: 3, name: 'ML Pipeline Order', description: 'Arrange the ML workflow in order', type: 'order', stars: 2,
        items: [
          { id: 'mlp1', text: 'Collect Data', order: 1 },
          { id: 'mlp2', text: 'Clean & Preprocess', order: 2 },
          { id: 'mlp3', text: 'Split into Train/Test', order: 3 },
          { id: 'mlp4', text: 'Choose a Model', order: 4 },
          { id: 'mlp5', text: 'Train the Model', order: 5 },
          { id: 'mlp6', text: 'Evaluate Performance', order: 6 },
          { id: 'mlp7', text: 'Deploy to Production', order: 7 },
        ]
      },
      { id: 4, name: 'ML Vocabulary', description: 'Fill in the blanks for ML terms', type: 'fill-blank', stars: 2,
        items: [
          { id: 'mlf1', sentence: 'In supervised learning, the output variable is called the ___.', answer: 'label' },
          { id: 'mlf2', sentence: 'The process of adjusting model parameters to minimize error is called ___.', answer: 'training' },
          { id: 'mlf3', sentence: 'When a model performs well on training data but poorly on new data, it is called ___.', answer: 'overfitting' },
          { id: 'mlf4', sentence: 'A ___ is a mathematical function that maps inputs to outputs.', answer: 'model' },
          { id: 'mlf5', sentence: 'The measure of how wrong the model predictions are is called the ___ function.', answer: 'loss' },
        ]
      },
      { id: 5, name: 'ML Key Concepts Quiz', description: 'Test your knowledge of ML concepts', type: 'quiz', stars: 2,
        questions: [
          { id: 'mlq1', question: 'Which type of ML uses labeled data?', options: ['Supervised', 'Unsupervised', 'Reinforcement', 'None'], answer: 0 },
          { id: 'mlq2', question: 'What is the goal of clustering?', options: ['Predict labels', 'Group similar data', 'Maximize rewards', 'Generate images'], answer: 1 },
          { id: 'mlq3', question: 'Which is NOT a common ML framework?', options: ['TensorFlow', 'PyTorch', 'Photoshop', 'Scikit-learn'], answer: 2 },
        ]
      },
      { id: 6, name: 'ML True or False', description: 'Rapid fire ML facts', type: 'true-false', stars: 3,
        items: [
          { id: 'mlt1', text: 'Machine Learning is a subset of Artificial Intelligence.', answer: true },
          { id: 'mlt2', text: 'All ML models require labeled data.', answer: false },
          { id: 'mlt3', text: 'More data always means a better model.', answer: false },
          { id: 'mlt4', text: 'Neural networks are inspired by the human brain.', answer: true },
          { id: 'mlt5', text: 'K-Means is a supervised learning algorithm.', answer: false },
          { id: 'mlt6', text: 'Feature engineering can improve model performance.', answer: true },
          { id: 'mlt7', text: 'Random Forest uses a single decision tree.', answer: false },
          { id: 'mlt8', text: 'Python is the most popular language for ML.', answer: true },
        ]
      },
      { id: 7, name: 'ML vs Traditional', description: 'Categorize approaches', type: 'categorize', stars: 2,
        categories: [
          { id: 'ml', label: 'Machine Learning', color: '#8b5cf6' },
          { id: 'traditional', label: 'Traditional Programming', color: '#f59e0b' },
        ],
        items: [
          { id: 'mlvt1', text: 'Learns patterns from data', category: 'ml' }, { id: 'mlvt2', text: 'Rules written by programmer', category: 'traditional' },
          { id: 'mlvt3', text: 'Improves with more data', category: 'ml' }, { id: 'mlvt4', text: 'Fixed logic (if-else)', category: 'traditional' },
          { id: 'mlvt5', text: 'Requires training phase', category: 'ml' }, { id: 'mlvt6', text: 'Deterministic output', category: 'traditional' },
          { id: 'mlvt7', text: 'Can handle unstructured data', category: 'ml' }, { id: 'mlvt8', text: 'Easy to debug', category: 'traditional' },
        ]
      },
      { id: 8, name: 'ML History', description: 'Order ML milestones', type: 'order', stars: 2,
        items: [
          { id: 'mlh1', text: 'Alan Turing proposes Turing Test (1950)', order: 1 },
          { id: 'mlh2', text: 'Perceptron invented (1958)', order: 2 },
          { id: 'mlh3', text: 'Backpropagation popularized (1986)', order: 3 },
          { id: 'mlh4', text: 'Deep Blue beats chess champion (1997)', order: 4 },
          { id: 'mlh5', text: 'AlexNet wins ImageNet (2012)', order: 5 },
          { id: 'mlh6', text: 'ChatGPT launches (2022)', order: 6 },
        ]
      },
      { id: 9, name: 'ML Frameworks Match', description: 'Match frameworks to their creators', type: 'match', stars: 2,
        items: [
          { id: 'mlfw1', left: 'TensorFlow', right: 'Google', match: 'mlfw1' },
          { id: 'mlfw2', left: 'PyTorch', right: 'Meta (Facebook)', match: 'mlfw2' },
          { id: 'mlfw3', left: 'Scikit-learn', right: 'Open source (Python)', match: 'mlfw3' },
          { id: 'mlfw4', left: 'Keras', right: 'High-level API for TensorFlow', match: 'mlfw4' },
          { id: 'mlfw5', left: 'Hugging Face', right: 'NLP model hub', match: 'mlfw5' },
        ]
      },
      { id: 10, name: 'ML Concepts Quiz', description: 'Advanced ML knowledge test', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is the bias-variance tradeoff?', options: ['Speed vs accuracy', 'Underfitting vs overfitting balance', 'Training vs testing', 'Size vs quality'], answer: 'Underfitting vs overfitting balance' },
          { question: 'Gradient descent is used to?', options: ['Generate data', 'Minimize the loss function', 'Split data', 'Visualize results'], answer: 'Minimize the loss function' },
          { question: 'What is a hyperparameter?', options: ['A learned parameter', 'A setting chosen before training', 'A type of data', 'An output value'], answer: 'A setting chosen before training' },
          { question: 'What is an ensemble method?', options: ['Using one strong model', 'Combining multiple models', 'Training without data', 'A type of loss function'], answer: 'Combining multiple models' },
          { question: 'Regularization helps prevent?', options: ['Underfitting', 'Overfitting', 'Data leakage', 'Feature scaling'], answer: 'Overfitting' },
        ]
      },
    ]
  },

  'ml-data-features': {
    title: 'Data & Features Lab',
    icon: Database,
    color: '#0ea5e9',
    info: 'Data is the fuel for ML. Feature engineering transforms raw data into meaningful inputs that help models learn better.',
    missions: [
      { id: 1, name: 'Data Types', description: 'Match data types to their examples', type: 'match', stars: 1,
        items: [
          { id: 'df1', left: 'Numerical', right: 'Age, Height, Temperature', match: 'df1' },
          { id: 'df2', left: 'Categorical', right: 'Color, Gender, Country', match: 'df2' },
          { id: 'df3', left: 'Text', right: 'Reviews, Tweets, Articles', match: 'df3' },
          { id: 'df4', left: 'Image', right: 'Photos, X-rays, Satellite', match: 'df4' },
          { id: 'df5', left: 'Time Series', right: 'Stock prices, Weather logs', match: 'df5' },
        ]
      },
      { id: 2, name: 'Data Cleaning Steps', description: 'Arrange data cleaning steps in order', type: 'order', stars: 2,
        items: [
          { id: 'dc1', text: 'Load the raw dataset', order: 1 },
          { id: 'dc2', text: 'Check for missing values', order: 2 },
          { id: 'dc3', text: 'Handle missing values (impute or drop)', order: 3 },
          { id: 'dc4', text: 'Remove duplicate rows', order: 4 },
          { id: 'dc5', text: 'Convert data types correctly', order: 5 },
          { id: 'dc6', text: 'Normalize or scale features', order: 6 },
        ]
      },
      { id: 3, name: 'Feature Engineering', description: 'Categorize feature engineering techniques', type: 'categorize', stars: 2,
        categories: [
          { id: 'create', label: 'Feature Creation', color: '#8b5cf6' },
          { id: 'transform', label: 'Transformation', color: '#f59e0b' },
          { id: 'select', label: 'Feature Selection', color: '#22c55e' },
        ],
        items: [
          { id: 'fe1', text: 'Extract year from date', category: 'create' },
          { id: 'fe2', text: 'Log transformation', category: 'transform' },
          { id: 'fe3', text: 'Remove correlated features', category: 'select' },
          { id: 'fe4', text: 'One-hot encoding', category: 'transform' },
          { id: 'fe5', text: 'Create BMI from height & weight', category: 'create' },
          { id: 'fe6', text: 'Use correlation matrix', category: 'select' },
          { id: 'fe7', text: 'Min-Max scaling', category: 'transform' },
          { id: 'fe8', text: 'Combine first & last name', category: 'create' },
          { id: 'fe9', text: 'Variance threshold', category: 'select' },
        ]
      },
      { id: 4, name: 'Data Splitting', description: 'Fill blanks about data splitting', type: 'fill-blank', stars: 2,
        items: [
          { id: 'ds1', sentence: 'The data used to train the model is called the ___ set.', answer: 'training' },
          { id: 'ds2', sentence: 'The data used to evaluate the final model is the ___ set.', answer: 'test' },
          { id: 'ds3', sentence: 'A common train-test split ratio is 80-___.', answer: '20' },
          { id: 'ds4', sentence: '___ validation splits data into k subsets for robust evaluation.', answer: 'Cross' },
          { id: 'ds5', sentence: 'Data ___ occurs when test data leaks into training.', answer: 'leakage' },
        ]
      },
      { id: 5, name: 'Common Datasets', description: 'Match famous datasets to their domains', type: 'match', stars: 1,
        items: [
          { id: 'cd1', left: 'MNIST', right: 'Handwritten digit images', match: 'cd1' },
          { id: 'cd2', left: 'Iris', right: 'Flower species classification', match: 'cd2' },
          { id: 'cd3', left: 'ImageNet', right: 'Large-scale object recognition', match: 'cd3' },
          { id: 'cd4', left: 'Titanic', right: 'Passenger survival prediction', match: 'cd4' },
          { id: 'cd5', left: 'CIFAR-10', right: '10-class image classification', match: 'cd5' },
        ]
      },
      { id: 6, name: 'Data Science True/False', description: 'Quick facts about data', type: 'true-false', stars: 3,
        items: [
          { id: 'dst1', text: 'Missing values should always be deleted.', answer: false },
          { id: 'dst2', text: 'Feature scaling helps algorithms like KNN.', answer: true },
          { id: 'dst3', text: 'Outliers never affect model performance.', answer: false },
          { id: 'dst4', text: 'One-hot encoding converts categories to numbers.', answer: true },
          { id: 'dst5', text: 'A larger dataset always guarantees better accuracy.', answer: false },
          { id: 'dst6', text: 'Train-test split prevents overfitting evaluation.', answer: true },
          { id: 'dst7', text: 'Pandas is a popular Python library for data manipulation.', answer: true },
          { id: 'dst8', text: 'Features and labels mean the same thing.', answer: false },
        ]
      },
      { id: 7, name: 'Data Visualization Match', description: 'Match chart types to their best use', type: 'match', stars: 2,
        items: [
          { id: 'dvm1', left: 'Scatter Plot', right: 'Show relationship between two variables', match: 'dvm1' },
          { id: 'dvm2', left: 'Histogram', right: 'Show distribution of single variable', match: 'dvm2' },
          { id: 'dvm3', left: 'Box Plot', right: 'Show median, quartiles, outliers', match: 'dvm3' },
          { id: 'dvm4', left: 'Heatmap', right: 'Show correlation between features', match: 'dvm4' },
          { id: 'dvm5', left: 'Bar Chart', right: 'Compare categories', match: 'dvm5' },
        ]
      },
      { id: 8, name: 'Missing Data Strategies', description: 'Categorize approaches to missing data', type: 'categorize', stars: 2,
        categories: [
          { id: 'remove', label: 'Removal Methods', color: '#ef4444' },
          { id: 'impute', label: 'Imputation Methods', color: '#22c55e' },
        ],
        items: [
          { id: 'dms1', text: 'Drop rows with NaN', category: 'remove' }, { id: 'dms2', text: 'Fill with mean', category: 'impute' },
          { id: 'dms3', text: 'Drop column if >50% missing', category: 'remove' }, { id: 'dms4', text: 'Fill with median', category: 'impute' },
          { id: 'dms5', text: 'Listwise deletion', category: 'remove' }, { id: 'dms6', text: 'Forward fill (time series)', category: 'impute' },
          { id: 'dms7', text: 'Pairwise deletion', category: 'remove' }, { id: 'dms8', text: 'KNN imputation', category: 'impute' },
        ]
      },
      { id: 9, name: 'Encoding Techniques', description: 'Order data encoding complexity', type: 'order', stars: 2,
        items: [
          { id: 'det1', text: 'Label encoding (assign numbers)', order: 1 },
          { id: 'det2', text: 'One-hot encoding (binary columns)', order: 2 },
          { id: 'det3', text: 'Ordinal encoding (preserve order)', order: 3 },
          { id: 'det4', text: 'Target encoding (mean of target)', order: 4 },
          { id: 'det5', text: 'Embedding (learned representation)', order: 5 },
        ]
      },
      { id: 10, name: 'Data Science Quiz', description: 'Advanced data handling test', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is a z-score?', options: ['Mean value', 'Number of standard deviations from mean', 'Median value', 'Mode value'], answer: 'Number of standard deviations from mean' },
          { question: 'PCA is used for?', options: ['Classification', 'Dimensionality reduction', 'Clustering', 'Regression'], answer: 'Dimensionality reduction' },
          { question: 'What is feature scaling?', options: ['Adding features', 'Normalizing feature ranges', 'Removing features', 'Creating features'], answer: 'Normalizing feature ranges' },
          { question: 'IQR helps detect?', options: ['Missing values', 'Outliers', 'Duplicates', 'Correlations'], answer: 'Outliers' },
          { question: 'Stratified sampling ensures?', options: ['Random selection', 'Equal class distribution in splits', 'No missing values', 'Feature independence'], answer: 'Equal class distribution in splits' },
        ]
      },
    ]
  },

  'ml-classification': {
    title: 'Classification Lab',
    icon: Sparkles,
    color: '#f59e0b',
    info: 'Classification is a supervised ML task where the model learns to assign categories or labels to new data based on past examples.',
    missions: [
      { id: 1, name: 'Classifier Types', description: 'Match classifiers to their descriptions', type: 'match', stars: 1,
        items: [
          { id: 'cl1', left: 'Decision Tree', right: 'Splits data using yes/no questions', match: 'cl1' },
          { id: 'cl2', left: 'KNN', right: 'Classifies based on nearest neighbors', match: 'cl2' },
          { id: 'cl3', left: 'Naive Bayes', right: 'Uses probability and Bayes theorem', match: 'cl3' },
          { id: 'cl4', left: 'SVM', right: 'Finds the best separating hyperplane', match: 'cl4' },
          { id: 'cl5', left: 'Random Forest', right: 'Ensemble of many decision trees', match: 'cl5' },
          { id: 'cl6', left: 'Logistic Regression', right: 'Predicts probability of a class', match: 'cl6' },
        ]
      },
      { id: 2, name: 'Classification vs Regression', description: 'Sort tasks into the right category', type: 'categorize', stars: 1,
        categories: [
          { id: 'classification', label: 'Classification', color: '#3b82f6' },
          { id: 'regression', label: 'Regression', color: '#ef4444' },
        ],
        items: [
          { id: 'cr1', text: 'Email: Spam or Not Spam', category: 'classification' },
          { id: 'cr2', text: 'Predict house price', category: 'regression' },
          { id: 'cr3', text: 'Cat vs Dog image', category: 'classification' },
          { id: 'cr4', text: 'Predict temperature', category: 'regression' },
          { id: 'cr5', text: 'Tumor: Benign or Malignant', category: 'classification' },
          { id: 'cr6', text: 'Predict stock price', category: 'regression' },
          { id: 'cr7', text: 'Handwritten digit (0-9)', category: 'classification' },
          { id: 'cr8', text: 'Predict salary', category: 'regression' },
        ]
      },
      { id: 3, name: 'Model Evaluation', description: 'Arrange model evaluation steps', type: 'order', stars: 2,
        items: [
          { id: 'me1', text: 'Split data into train and test', order: 1 },
          { id: 'me2', text: 'Train the classifier', order: 2 },
          { id: 'me3', text: 'Make predictions on test set', order: 3 },
          { id: 'me4', text: 'Compare predictions vs actual labels', order: 4 },
          { id: 'me5', text: 'Calculate accuracy score', order: 5 },
          { id: 'me6', text: 'Generate confusion matrix', order: 6 },
        ]
      },
      { id: 4, name: 'Classification Metrics', description: 'Fill blanks about evaluation metrics', type: 'fill-blank', stars: 2,
        items: [
          { id: 'cm1', sentence: '___ measures the ratio of correct predictions to total predictions.', answer: 'Accuracy' },
          { id: 'cm2', sentence: 'Of all positive predictions, how many were actually positive is called ___.', answer: 'Precision' },
          { id: 'cm3', sentence: 'Of all actual positives, how many were correctly predicted is called ___.', answer: 'Recall' },
          { id: 'cm4', sentence: 'The F1 score is the harmonic ___ of precision and recall.', answer: 'mean' },
          { id: 'cm5', sentence: 'A ___ matrix shows true/false positives and negatives.', answer: 'confusion' },
        ]
      },
      { id: 5, name: 'Decision Tree Quiz', description: 'Test your decision tree knowledge', type: 'quiz', stars: 2,
        questions: [
          { id: 'dtq1', question: 'What does a leaf node represent in a decision tree?', options: ['A question', 'A decision/class', 'A feature', 'An error'], answer: 1 },
          { id: 'dtq2', question: 'What measures impurity in decision trees?', options: ['Accuracy', 'Gini Index', 'Loss', 'Gradient'], answer: 1 },
          { id: 'dtq3', question: 'What is pruning in decision trees?', options: ['Adding more nodes', 'Removing unnecessary branches', 'Splitting data', 'Scaling features'], answer: 1 },
        ]
      },
      { id: 6, name: 'Classification True/False', description: 'Quick classification facts', type: 'true-false', stars: 3,
        items: [
          { id: 'ctf1', text: 'KNN is a lazy learning algorithm (no training phase).', answer: true },
          { id: 'ctf2', text: 'Decision Trees can only solve binary classification.', answer: false },
          { id: 'ctf3', text: 'High accuracy always means a good model.', answer: false },
          { id: 'ctf4', text: 'Random Forest reduces overfitting compared to a single tree.', answer: true },
          { id: 'ctf5', text: 'Logistic Regression is only used for regression tasks.', answer: false },
          { id: 'ctf6', text: 'SVM works well in high-dimensional spaces.', answer: true },
          { id: 'ctf7', text: 'The value of K in KNN doesn\'t affect results.', answer: false },
          { id: 'ctf8', text: 'Naive Bayes assumes features are independent.', answer: true },
        ]
      },
      { id: 7, name: 'Confusion Matrix', description: 'Match confusion matrix terms', type: 'match', stars: 3,
        items: [
          { id: 'ccm1', left: 'True Positive (TP)', right: 'Correctly predicted positive', match: 'ccm1' },
          { id: 'ccm2', left: 'False Positive (FP)', right: 'Incorrectly predicted positive', match: 'ccm2' },
          { id: 'ccm3', left: 'True Negative (TN)', right: 'Correctly predicted negative', match: 'ccm3' },
          { id: 'ccm4', left: 'False Negative (FN)', right: 'Missed a real positive case', match: 'ccm4' },
          { id: 'ccm5', left: 'Recall', right: 'TP / (TP + FN)', match: 'ccm5' },
        ]
      },
      { id: 8, name: 'Model Selection', description: 'Order the model selection process', type: 'order', stars: 2,
        items: [
          { id: 'cms1', text: 'Understand the problem type (binary/multi-class)', order: 1 },
          { id: 'cms2', text: 'Choose candidate algorithms', order: 2 },
          { id: 'cms3', text: 'Train each model', order: 3 },
          { id: 'cms4', text: 'Compare using cross-validation', order: 4 },
          { id: 'cms5', text: 'Tune hyperparameters of best model', order: 5 },
          { id: 'cms6', text: 'Evaluate on held-out test set', order: 6 },
        ]
      },
      { id: 9, name: 'When to Use Which', description: 'Categorize classifiers by best use case', type: 'categorize', stars: 3,
        categories: [
          { id: 'simple', label: 'Simple / Small Data', color: '#22c55e' },
          { id: 'complex', label: 'Complex / Large Data', color: '#8b5cf6' },
        ],
        items: [
          { id: 'cwu1', text: 'Logistic Regression', category: 'simple' }, { id: 'cwu2', text: 'Deep Neural Network', category: 'complex' },
          { id: 'cwu3', text: 'Naive Bayes', category: 'simple' }, { id: 'cwu4', text: 'Random Forest', category: 'complex' },
          { id: 'cwu5', text: 'KNN (k=3)', category: 'simple' }, { id: 'cwu6', text: 'Gradient Boosting (XGBoost)', category: 'complex' },
          { id: 'cwu7', text: 'Decision Stump', category: 'simple' }, { id: 'cwu8', text: 'SVM with RBF kernel', category: 'complex' },
        ]
      },
      { id: 10, name: 'Classification Challenge Quiz', description: 'Advanced classification test', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is the ROC curve?', options: ['A loss function', 'Plot of TPR vs FPR', 'A type of neural network', 'A data split method'], answer: 'Plot of TPR vs FPR' },
          { question: 'What does AUC measure?', options: ['Speed', 'Area Under the ROC Curve', 'Accuracy', 'Loss'], answer: 'Area Under the ROC Curve' },
          { question: 'Ensemble learning combines?', options: ['Datasets', 'Multiple models', 'Features', 'Labels'], answer: 'Multiple models' },
          { question: 'Which handles imbalanced datasets well?', options: ['Accuracy', 'F1 Score', 'MSE', 'R-squared'], answer: 'F1 Score' },
          { question: 'What is bagging?', options: ['Bootstrap Aggregating', 'Batch Averaging', 'Binary Grouping', 'Balanced Augmenting'], answer: 'Bootstrap Aggregating' },
        ]
      },
    ]
  },

  'ml-neural-networks': {
    title: 'Neural Networks Lab',
    icon: Network,
    color: '#ec4899',
    info: 'Neural networks are computing systems inspired by the brain. They consist of layers of connected nodes (neurons) that learn to recognize patterns.',
    missions: [
      { id: 1, name: 'NN Components', description: 'Match neural network components', type: 'match', stars: 1,
        items: [
          { id: 'nn1', left: 'Input Layer', right: 'Receives the raw data features', match: 'nn1' },
          { id: 'nn2', left: 'Hidden Layer', right: 'Processes data between input and output', match: 'nn2' },
          { id: 'nn3', left: 'Output Layer', right: 'Produces the final prediction', match: 'nn3' },
          { id: 'nn4', left: 'Weights', right: 'Parameters adjusted during training', match: 'nn4' },
          { id: 'nn5', left: 'Activation Function', right: 'Adds non-linearity to the network', match: 'nn5' },
          { id: 'nn6', left: 'Bias', right: 'Extra parameter for flexibility', match: 'nn6' },
        ]
      },
      { id: 2, name: 'How a NN Learns', description: 'Arrange the training process in order', type: 'order', stars: 2,
        items: [
          { id: 'nnl1', text: 'Initialize random weights', order: 1 },
          { id: 'nnl2', text: 'Forward pass: compute prediction', order: 2 },
          { id: 'nnl3', text: 'Calculate loss (error)', order: 3 },
          { id: 'nnl4', text: 'Backward pass: compute gradients', order: 4 },
          { id: 'nnl5', text: 'Update weights using optimizer', order: 5 },
          { id: 'nnl6', text: 'Repeat for many epochs', order: 6 },
        ]
      },
      { id: 3, name: 'Activation Functions', description: 'Categorize activation functions by type', type: 'categorize', stars: 2,
        categories: [
          { id: 'common', label: 'Commonly Used', color: '#22c55e' },
          { id: 'older', label: 'Older / Less Common', color: '#f59e0b' },
        ],
        items: [
          { id: 'af1', text: 'ReLU', category: 'common' },
          { id: 'af2', text: 'Sigmoid', category: 'older' },
          { id: 'af3', text: 'Softmax', category: 'common' },
          { id: 'af4', text: 'Tanh', category: 'older' },
          { id: 'af5', text: 'Leaky ReLU', category: 'common' },
          { id: 'af6', text: 'Step Function', category: 'older' },
          { id: 'af7', text: 'GELU', category: 'common' },
          { id: 'af8', text: 'Swish', category: 'common' },
        ]
      },
      { id: 4, name: 'Deep Learning Terms', description: 'Fill in key deep learning terms', type: 'fill-blank', stars: 2,
        items: [
          { id: 'dl1', sentence: 'A neural network with multiple hidden layers is called a ___ neural network.', answer: 'deep' },
          { id: 'dl2', sentence: 'The algorithm that adjusts weights by computing gradients is called ___.', answer: 'backpropagation' },
          { id: 'dl3', sentence: 'One complete pass through the entire training dataset is called an ___.', answer: 'epoch' },
          { id: 'dl4', sentence: 'The ___ rate controls how much weights are updated each step.', answer: 'learning' },
          { id: 'dl5', sentence: 'A ___ is a subset of training data used in one weight update.', answer: 'batch' },
        ]
      },
      { id: 5, name: 'NN Architectures', description: 'Match architectures to their use cases', type: 'match', stars: 2,
        items: [
          { id: 'na1', left: 'CNN', right: 'Image recognition and computer vision', match: 'na1' },
          { id: 'na2', left: 'RNN', right: 'Sequential data like text or speech', match: 'na2' },
          { id: 'na3', left: 'GAN', right: 'Generating realistic fake data', match: 'na3' },
          { id: 'na4', left: 'Transformer', right: 'Language models like GPT and BERT', match: 'na4' },
          { id: 'na5', left: 'Autoencoder', right: 'Compressing and reconstructing data', match: 'na5' },
        ]
      },
      { id: 6, name: 'Neural Networks True/False', description: 'Quick neural network facts', type: 'true-false', stars: 3,
        items: [
          { id: 'nntf1', text: 'A neural network with 0 hidden layers is called a perceptron.', answer: true },
          { id: 'nntf2', text: 'More layers always guarantee better performance.', answer: false },
          { id: 'nntf3', text: 'GPUs significantly speed up neural network training.', answer: true },
          { id: 'nntf4', text: 'CNNs are primarily used for text analysis.', answer: false },
          { id: 'nntf5', text: 'Dropout is a technique to prevent overfitting.', answer: true },
          { id: 'nntf6', text: 'Transformers use attention mechanisms.', answer: true },
          { id: 'nntf7', text: 'ReLU outputs negative values for negative inputs.', answer: false },
          { id: 'nntf8', text: 'Transfer learning saves time by reusing trained models.', answer: true },
        ]
      },
      { id: 7, name: 'CNN Components', description: 'Match CNN layers to their roles', type: 'match', stars: 3,
        items: [
          { id: 'cnn1', left: 'Convolutional Layer', right: 'Extracts features using filters', match: 'cnn1' },
          { id: 'cnn2', left: 'Pooling Layer', right: 'Reduces spatial dimensions', match: 'cnn2' },
          { id: 'cnn3', left: 'Flatten Layer', right: 'Converts 2D to 1D vector', match: 'cnn3' },
          { id: 'cnn4', left: 'Dense/FC Layer', right: 'Fully connected classification', match: 'cnn4' },
          { id: 'cnn5', left: 'Dropout Layer', right: 'Randomly deactivates neurons', match: 'cnn5' },
        ]
      },
      { id: 8, name: 'Training Techniques', description: 'Categorize training strategies', type: 'categorize', stars: 2,
        categories: [
          { id: 'prevent', label: 'Prevent Overfitting', color: '#22c55e' },
          { id: 'speed', label: 'Speed Up Training', color: '#3b82f6' },
        ],
        items: [
          { id: 'ntt1', text: 'Dropout', category: 'prevent' }, { id: 'ntt2', text: 'GPU acceleration', category: 'speed' },
          { id: 'ntt3', text: 'Early stopping', category: 'prevent' }, { id: 'ntt4', text: 'Batch normalization', category: 'speed' },
          { id: 'ntt5', text: 'Data augmentation', category: 'prevent' }, { id: 'ntt6', text: 'Learning rate scheduling', category: 'speed' },
          { id: 'ntt7', text: 'L2 regularization', category: 'prevent' }, { id: 'ntt8', text: 'Mixed precision training', category: 'speed' },
        ]
      },
      { id: 9, name: 'Neural Network Build', description: 'Order steps to build a NN', type: 'order', stars: 2,
        items: [
          { id: 'nnb1', text: 'Define the architecture (layers)', order: 1 },
          { id: 'nnb2', text: 'Choose activation functions', order: 2 },
          { id: 'nnb3', text: 'Select loss function', order: 3 },
          { id: 'nnb4', text: 'Pick an optimizer (Adam, SGD)', order: 4 },
          { id: 'nnb5', text: 'Train on data with batches', order: 5 },
          { id: 'nnb6', text: 'Evaluate and fine-tune', order: 6 },
        ]
      },
      { id: 10, name: 'Deep Learning Quiz', description: 'Advanced deep learning test', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is vanishing gradient problem?', options: ['Gradients become too large', 'Gradients become near zero', 'Gradients oscillate', 'No gradients computed'], answer: 'Gradients become near zero' },
          { question: 'LSTM is designed to handle?', options: ['Images', 'Long-term dependencies in sequences', 'Tabular data', 'Audio only'], answer: 'Long-term dependencies in sequences' },
          { question: 'What is attention mechanism?', options: ['Focusing on relevant parts of input', 'A loss function', 'A type of layer', 'A dataset'], answer: 'Focusing on relevant parts of input' },
          { question: 'Batch size affects?', options: ['Only accuracy', 'Training speed and memory', 'Only the dataset', 'Nothing'], answer: 'Training speed and memory' },
          { question: 'What optimizer is most commonly used today?', options: ['SGD', 'Adam', 'RMSprop', 'Adagrad'], answer: 'Adam' },
        ]
      },
    ]
  },

  'ml-ethics': {
    title: 'AI Ethics & Bias Lab',
    icon: ShieldAlert,
    color: '#ef4444',
    info: 'AI Ethics examines fairness, accountability, and transparency in AI systems. Biased data leads to biased models that can harm people.',
    missions: [
      { id: 1, name: 'Types of AI Bias', description: 'Match bias types to their definitions', type: 'match', stars: 1,
        items: [
          { id: 'ab1', left: 'Selection Bias', right: 'Training data doesn\'t represent the real world', match: 'ab1' },
          { id: 'ab2', left: 'Confirmation Bias', right: 'Model reinforces existing stereotypes', match: 'ab2' },
          { id: 'ab3', left: 'Measurement Bias', right: 'Data collected inaccurately or unevenly', match: 'ab3' },
          { id: 'ab4', left: 'Algorithmic Bias', right: 'Model design favors certain outcomes', match: 'ab4' },
          { id: 'ab5', left: 'Historical Bias', right: 'Past societal biases reflected in data', match: 'ab5' },
        ]
      },
      { id: 2, name: 'Ethical AI Principles', description: 'Sort principles into correct categories', type: 'categorize', stars: 1,
        categories: [
          { id: 'fair', label: 'Fairness', color: '#3b82f6' },
          { id: 'trans', label: 'Transparency', color: '#22c55e' },
          { id: 'priv', label: 'Privacy', color: '#ef4444' },
        ],
        items: [
          { id: 'ep1', text: 'Equal treatment regardless of race', category: 'fair' },
          { id: 'ep2', text: 'Explainable model decisions', category: 'trans' },
          { id: 'ep3', text: 'Anonymizing personal data', category: 'priv' },
          { id: 'ep4', text: 'Unbiased training data', category: 'fair' },
          { id: 'ep5', text: 'Open-source model weights', category: 'trans' },
          { id: 'ep6', text: 'GDPR compliance', category: 'priv' },
          { id: 'ep7', text: 'No discrimination in hiring AI', category: 'fair' },
          { id: 'ep8', text: 'Audit trails for decisions', category: 'trans' },
          { id: 'ep9', text: 'Data minimization', category: 'priv' },
        ]
      },
      { id: 3, name: 'Responsible AI Steps', description: 'Arrange steps for building responsible AI', type: 'order', stars: 2,
        items: [
          { id: 'ra1', text: 'Define the problem and stakeholders', order: 1 },
          { id: 'ra2', text: 'Audit training data for bias', order: 2 },
          { id: 'ra3', text: 'Design fair evaluation metrics', order: 3 },
          { id: 'ra4', text: 'Build with diverse team input', order: 4 },
          { id: 'ra5', text: 'Test on underrepresented groups', order: 5 },
          { id: 'ra6', text: 'Monitor and update post-deployment', order: 6 },
        ]
      },
      { id: 4, name: 'AI Ethics Vocabulary', description: 'Fill in key ethics terms', type: 'fill-blank', stars: 2,
        items: [
          { id: 'ae1', sentence: 'The ability to understand why an AI made a decision is called ___.', answer: 'explainability' },
          { id: 'ae2', sentence: 'A ___ is a synthetic image or video created by AI to deceive.', answer: 'deepfake' },
          { id: 'ae3', sentence: 'GDPR stands for General Data ___ Regulation.', answer: 'Protection' },
          { id: 'ae4', sentence: 'AI ___ means being responsible for the outcomes of AI systems.', answer: 'accountability' },
          { id: 'ae5', sentence: 'When AI produces plausible but false information, it is called ___.', answer: 'hallucination' },
        ]
      },
      { id: 5, name: 'Real-World AI Cases', description: 'Match AI controversies to their domains', type: 'match', stars: 2,
        items: [
          { id: 'rc1', left: 'Hiring Algorithm Bias', right: 'Gender discrimination in resume screening', match: 'rc1' },
          { id: 'rc2', left: 'Facial Recognition', right: 'Higher error rates for darker skin tones', match: 'rc2' },
          { id: 'rc3', left: 'Predictive Policing', right: 'Reinforcing racial profiling patterns', match: 'rc3' },
          { id: 'rc4', left: 'Healthcare AI', right: 'Under-diagnosing minority patients', match: 'rc4' },
          { id: 'rc5', left: 'Credit Scoring', right: 'Denying loans based on zip code', match: 'rc5' },
        ]
      },
      { id: 6, name: 'AI Ethics True/False', description: 'Quick facts about AI responsibility', type: 'true-false', stars: 3,
        items: [
          { id: 'aetf1', text: 'AI systems can never be biased if the algorithm is correct.', answer: false },
          { id: 'aetf2', text: 'Diverse training data helps reduce bias.', answer: true },
          { id: 'aetf3', text: 'Deepfakes can only be used for entertainment.', answer: false },
          { id: 'aetf4', text: 'Companies should be accountable for their AI decisions.', answer: true },
          { id: 'aetf5', text: 'AI can make perfectly objective decisions without human values.', answer: false },
          { id: 'aetf6', text: 'Privacy-preserving techniques like federated learning exist.', answer: true },
          { id: 'aetf7', text: 'Regulation of AI is unnecessary.', answer: false },
          { id: 'aetf8', text: 'Informed consent matters when using personal data for AI.', answer: true },
        ]
      },
      { id: 7, name: 'AI Regulation', description: 'Match regulations to their regions', type: 'match', stars: 2,
        items: [
          { id: 'reg1', left: 'GDPR', right: 'European Union data protection', match: 'reg1' },
          { id: 'reg2', left: 'AI Act', right: 'EU regulation for AI systems', match: 'reg2' },
          { id: 'reg3', left: 'CCPA', right: 'California consumer privacy', match: 'reg3' },
          { id: 'reg4', left: 'NITI Aayog AI Strategy', right: 'India AI for All approach', match: 'reg4' },
          { id: 'reg5', left: 'UNESCO AI Ethics', right: 'Global AI ethics recommendations', match: 'reg5' },
        ]
      },
      { id: 8, name: 'Bias Detection Steps', description: 'Order bias audit process', type: 'order', stars: 3,
        items: [
          { id: 'abd1', text: 'Define fairness metrics for the problem', order: 1 },
          { id: 'abd2', text: 'Analyze training data demographics', order: 2 },
          { id: 'abd3', text: 'Evaluate model across different groups', order: 3 },
          { id: 'abd4', text: 'Identify disparities in predictions', order: 4 },
          { id: 'abd5', text: 'Apply mitigation techniques', order: 5 },
          { id: 'abd6', text: 'Re-evaluate and document findings', order: 6 },
        ]
      },
      { id: 9, name: 'Good vs Bad AI', description: 'Categorize AI use cases', type: 'categorize', stars: 2,
        categories: [
          { id: 'good', label: 'Beneficial AI Use', color: '#22c55e' },
          { id: 'harmful', label: 'Harmful AI Use', color: '#ef4444' },
        ],
        items: [
          { id: 'gba1', text: 'Early disease detection', category: 'good' }, { id: 'gba2', text: 'Mass surveillance without consent', category: 'harmful' },
          { id: 'gba3', text: 'Disaster prediction', category: 'good' }, { id: 'gba4', text: 'Deepfake misinformation', category: 'harmful' },
          { id: 'gba5', text: 'Personalized education', category: 'good' }, { id: 'gba6', text: 'Autonomous weapons', category: 'harmful' },
          { id: 'gba7', text: 'Accessibility tools', category: 'good' }, { id: 'gba8', text: 'Manipulative algorithms', category: 'harmful' },
        ]
      },
      { id: 10, name: 'AI Ethics Challenge Quiz', description: 'Advanced ethics test', type: 'quiz', stars: 3,
        questions: [
          { question: 'What is algorithmic accountability?', options: ['Speed of algorithm', 'Responsibility for AI decisions', 'Algorithm complexity', 'Code review'], answer: 'Responsibility for AI decisions' },
          { question: 'Federated learning helps with?', options: ['Speed', 'Privacy-preserving model training', 'Data generation', 'Visualization'], answer: 'Privacy-preserving model training' },
          { question: 'What is a model card?', options: ['A credit card for ML', 'Documentation of model performance and limitations', 'A training dataset', 'A type of neural network'], answer: 'Documentation of model performance and limitations' },
          { question: 'Right to explanation means?', options: ['Right to free AI', 'Users can ask why AI made a decision', 'Right to data', 'Right to delete AI'], answer: 'Users can ask why AI made a decision' },
          { question: 'Which principle says AI should not harm people?', options: ['Efficiency', 'Beneficence and non-maleficence', 'Transparency', 'Scalability'], answer: 'Beneficence and non-maleficence' },
        ]
      },
    ]
  },
};
